import './propsHualien';
import { wallDecorTown } from './propsTown';
import { drawExtraDecor } from './propRegistry';
import type { Decor, ObjectId, Rect } from '../world/mapdef';
import { OUTLINE, block, ellipse, rect, shadow, shade, sun, type Ctx } from './paint';
import { wind } from './nature';

/** Props for the trips: the town station, Taipei (101), the MRT, the Palace Museum and Hualien. */
type Drawer = (c: Ctx, r: Rect, t: number) => void;

const CJK = '"PingFang TC", "Noto Sans TC", "Noto Sans CJK TC", "Microsoft JhengHei", sans-serif';
const T = 16;

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function txt(c: Ctx, s: string, x: number, y: number, size: number, col: string, weight = 900, shadowCol?: string): void {
  c.font = `${weight} ${size}px ${CJK}`;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  if (shadowCol) {
    c.fillStyle = shadowCol;
    c.fillText(s, x + 0.4, y + 0.5);
  }
  c.fillStyle = col;
  c.fillText(s, x, y);
}

function sign(c: Ctx, text: string, cx: number, y: number, w: number, bg: string, fg = '#fff6dc'): void {
  block(c, cx - w / 2, y, w, 11, bg, shade(bg, 0.25), shade(bg, -0.3));
  txt(c, text, cx, y + 5.9, 8, fg, 900, 'rgba(0,0,0,0.35)');
}

function vgrad(c: Ctx, x: number, y: number, w: number, h: number, top: string, bottom: string): void {
  const g = c.createLinearGradient(0, y, 0, y + h);
  g.addColorStop(0, top);
  g.addColorStop(1, bottom);
  c.fillStyle = g;
  c.fillRect(x, y, w, h);
}

function poly(c: Ctx, col: string, pts: number[]): void {
  c.fillStyle = col;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.closePath();
  c.fill();
}

function line(c: Ctx, col: string, w: number, pts: number[]): void {
  c.strokeStyle = col;
  c.lineWidth = w;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.stroke();
}

const isNight = () => sun.alpha < 0.8;

/** A night-market style stall (same look as props3's): counter, striped canopy, glowing sign, bulb. */
function stall(c: Ctx, r: Rect, t: number, label: string, canopy: string, goods: (c: Ctx, r: Rect, t: number) => void): void {
  const { x, y, w, h } = r;
  shadow(c, x + w / 2, y + h, w / 2 + 2, 3.5, 0.35);
  rect(c, OUTLINE, x + 1, y - 34, 2, 36);
  rect(c, OUTLINE, x + w - 3, y - 34, 2, 36);
  const top = y - 40;
  for (let i = 0; i < w; i += 6) {
    c.fillStyle = (i / 6) % 2 ? '#fff6e6' : canopy;
    c.fillRect(x - 2 + i, top, Math.min(6, w + 4 - i), 9);
    c.beginPath();
    c.arc(x + 1 + i, top + 9, 3, 0, Math.PI);
    c.fill();
  }
  rect(c, 'rgba(0,0,0,0.25)', x - 2, top + 9, w + 4, 1);
  sign(c, label, x + w / 2, top - 11, Math.max(28, label.length * 9 + 8), '#c0392b');
  const sw = wind.at(x, y) * 1.2 + Math.sin(t * 1.7 + x) * 0.6;
  line(c, OUTLINE, 0.6, [x + w / 2, top + 9, x + w / 2 + sw, top + 16]);
  ellipse(c, '#fff3b0', x + w / 2 + sw, top + 17.5, 1.8, 2);
  ellipse(c, 'rgba(255,230,140,0.25)', x + w / 2 + sw, top + 18, 6, 5);
  goods(c, r, t);
  block(c, x, y - 8, w, h + 8, '#9a6a44', '#b88a5c', '#6e4a2e');
  rect(c, '#f2e6cf', x + 1, y - 9, w - 2, 2);
  for (let i = 4; i < w - 2; i += 8) rect(c, 'rgba(0,0,0,0.15)', x + i, y - 6, 0.6, h + 4);
}

function steam(c: Ctx, x: number, y: number, t: number): void {
  for (let i = 0; i < 3; i++) {
    const k = (t * 0.6 + i / 3) % 1;
    ellipse(c, `rgba(255,255,255,${0.35 * (1 - k)})`, x + Math.sin(t * 2 + i * 2) * 2, y - k * 12, 1.5 + k * 2, 1.2 + k * 1.5);
  }
}

/** A white waterfall from y0 to y1 (width w) with moving streaks, a foaming pool and spray. */
function fall(c: Ctx, x: number, y0: number, y1: number, w: number, t: number): void {
  const h = y1 - y0;
  // The creek that feeds it, winding over the cliff top.
  c.strokeStyle = 'rgba(110,200,210,0.85)';
  c.lineWidth = 2.2;
  c.beginPath();
  c.moveTo(x - 6, y0 - 22);
  c.quadraticCurveTo(x + 6, y0 - 14, x - 2, y0 - 8);
  c.quadraticCurveTo(x - 6, y0 - 4, x, y0);
  c.stroke();
  c.strokeStyle = 'rgba(255,255,255,0.6)';
  c.lineWidth = 0.5;
  c.stroke();
  // Wet, dark rock around the fall and the notch it pours from.
  vgrad(c, x - w, y0 - 2, w * 2, h, 'rgba(50,58,58,0.35)', 'rgba(50,58,58,0.15)');
  ellipse(c, 'rgba(40,46,44,0.6)', x, y0, w * 0.8, 2.4);
  ellipse(c, '#3f7a35', x - w * 0.9, y0 - 1, 3, 1.6);
  ellipse(c, '#4f8a3f', x + w * 0.9, y0 + 1, 2.6, 1.4);
  // Falling sheet: wobbly edges, brighter at the core.
  c.fillStyle = 'rgba(225,245,250,0.9)';
  c.beginPath();
  c.moveTo(x - w / 2 + 1, y0);
  for (let k = 0; k <= 1.001; k += 0.1) c.lineTo(x - w / 2 - k * 1.6 + Math.sin(k * 17 + t * 3) * 0.4, y0 + k * h);
  for (let k = 1; k >= -0.001; k -= 0.1) c.lineTo(x + w / 2 + k * 1.6 + Math.sin(k * 13 + t * 3 + 1) * 0.4, y0 + k * h);
  c.closePath();
  c.fill();
  rect(c, 'rgba(255,255,255,0.85)', x - w / 4, y0, w / 2, h);
  const n = Math.max(6, Math.round(h / 10));
  for (let i = 0; i < n; i++) {
    const k = (t * 0.8 + i / n) % 1;
    const sx = x - w / 2 + 1 + ((i * 7) % Math.max(1, w - 2));
    rect(c, 'rgba(120,175,195,0.55)', sx, y0 + k * (h - 6), 0.6, 6);
  }
  // Plunge pool, foam ring and drifting spray.
  ellipse(c, 'rgba(70,170,180,0.9)', x, y1, w + 6, 3.4);
  ellipse(c, 'rgba(140,215,220,0.9)', x, y1 - 0.4, w + 4, 2.4);
  ellipse(c, 'rgba(255,255,255,0.9)', x, y1 - 0.6, w * 0.9, 1.6);
  for (let i = 0; i < 6; i++) {
    const k = (t * 1.1 + i / 6) % 1;
    const a = i * 1.05;
    ellipse(c, `rgba(255,255,255,${0.55 * (1 - k)})`, x + Math.cos(a) * (2 + k * (w + 2)), y1 - 1.5 - k * 7, 1.2 + k * 2.2, 1 + k * 1.4);
  }
}

/** Offscreen cache for big static drawings that live on outdoor maps (drawn every frame otherwise). */
const bitmaps = new Map<string, { cv: HTMLCanvasElement; s: number }>();
function cachedArt(c: Ctx, key: string, x0: number, y0: number, w: number, h: number, draw: (cc: Ctx) => void): void {
  const m = c.getTransform();
  const s = Math.max(1, Math.round(Math.hypot(m.a, m.b) * 2) / 2);
  let e = bitmaps.get(key);
  if (!e || e.s !== s) {
    const cv = document.createElement('canvas');
    cv.width = Math.ceil(w * s);
    cv.height = Math.ceil(h * s);
    const cc = cv.getContext('2d')!;
    cc.scale(s, s);
    cc.translate(-x0, -y0);
    draw(cc);
    e = { cv, s };
    bitmaps.set(key, e);
  }
  c.drawImage(e.cv, x0, y0, w, h);
}

// ---------------------------------------------------------------------------
// Trains
// ---------------------------------------------------------------------------

interface TrainStyle {
  body: string;
  roof: string;
  stripe: string;
  stripe2: string;
  car: number;
  h: number;
  doorEvery: number;
}

const EMU: TrainStyle = { body: '#f4f5f2', roof: '#c9cdd0', stripe: '#f08a24', stripe2: '#2f5fa8', car: 96, h: 34, doorEvery: 48 };
const METRO: TrainStyle = { body: '#e6eaee', roof: '#b9c0c6', stripe: '#1f6fc0', stripe2: '#5fc0e8', car: 96, h: 40, doorEvery: 48 };

/**
 * A side-on train from x0 to x1 with its wheels on `by`. Doors sit at `doorX + k*doorEvery`;
 * `open` (0..1) slides their leaves apart. Cabs are drawn at whichever ends are `cabL`/`cabR`.
 */
function train(c: Ctx, st: TrainStyle, x0: number, x1: number, by: number, t: number, doorX: number, open: number, cabL: boolean, cabR: boolean, label?: string): void {
  const H = st.h, top = by - H;
  const yWin = top + 9, hWin = Math.round(H * 0.3);
  const yStripe = yWin + hWin + 2;
  // Bogies and wheels.
  for (let x = x0 + 14; x < x1 - 8; x += st.car / 2) {
    rect(c, '#2b2e33', x - 7, by - 4, 14, 3);
    ellipse(c, '#3a3e44', x - 4, by - 1.6, 2.2, 2.2);
    ellipse(c, '#3a3e44', x + 4, by - 1.6, 2.2, 2.2);
    ellipse(c, '#8a9096', x - 4, by - 1.6, 0.7, 0.7);
    ellipse(c, '#8a9096', x + 4, by - 1.6, 0.7, 0.7);
  }
  rect(c, '#3a3e44', x0 + 2, by - 6, x1 - x0 - 4, 2.5);
  // Body.
  c.fillStyle = shade(st.body, -0.55);
  c.beginPath();
  c.roundRect(x0, top, x1 - x0, H - 5, 3);
  c.fill();
  vgrad(c, x0 + 0.6, top + 0.6, x1 - x0 - 1.2, H - 6.2, '#ffffff', shade(st.body, -0.12));
  // Roof seen a little from above, with AC pods and a pantograph.
  vgrad(c, x0 + 1, top + 0.6, x1 - x0 - 2, 4, st.roof, shade(st.roof, -0.15));
  rect(c, 'rgba(255,255,255,0.5)', x0 + 2, top + 0.8, x1 - x0 - 4, 0.6);
  for (let x = x0 + st.car / 2; x < x1 - 10; x += st.car) {
    block(c, x - 9, top - 2.5, 18, 3.5, '#d8dcdf', '#eef0f2', '#a8aeb3');
  }
  if (st === EMU) {
    const px = x0 + st.car * 1.5;
    if (px < x1 - 20) {
      line(c, '#4a4e54', 0.7, [px - 6, top - 1, px, top - 7, px + 6, top - 1]);
      rect(c, '#4a4e54', px - 5, top - 8, 10, 0.8);
    }
  }
  // Window band.
  vgrad(c, x0 + 3, yWin, x1 - x0 - 6, hWin, '#2c3a48', '#141c24');
  // Sky reflections.
  c.fillStyle = 'rgba(160,210,255,0.22)';
  for (let x = x0 + 6; x < x1 - 8; x += 22) {
    c.beginPath();
    c.moveTo(x, yWin + hWin);
    c.lineTo(x + 5, yWin);
    c.lineTo(x + 9, yWin);
    c.lineTo(x + 4, yWin + hWin);
    c.fill();
  }
  // Livery stripes.
  rect(c, st.stripe, x0 + 1, yStripe, x1 - x0 - 2, 2.2);
  rect(c, st.stripe2, x0 + 1, yStripe + 2.8, x1 - x0 - 2, 1);
  rect(c, shade(st.body, -0.25), x0 + 1, by - 8, x1 - x0 - 2, 1);
  // Car joints.
  for (let x = x0 + st.car; x < x1 - 4; x += st.car) {
    rect(c, '#2b2e33', x - 1, top + 2, 2, H - 8);
    rect(c, 'rgba(0,0,0,0.25)', x + 1, top + 2, 0.6, H - 8);
  }
  // Doors.
  const lamp = Math.sin(t * 6) > 0;
  for (let k = Math.ceil((x0 + 10 - doorX) / st.doorEvery); ; k++) {
    const dx = doorX + k * st.doorEvery;
    if (dx > x1 - 10) break;
    if (Math.abs(((dx - x0) % st.car) - 0) < 6) continue;
    const dw = 6 * (1 - open);
    rect(c, '#20262c', dx - 7, yWin - 2, 14, by - 7 - yWin + 2);
    if (open > 0.02) vgrad(c, dx - 6, yWin - 1, 12, by - 8 - yWin + 1, '#f2e8c8', '#c8bc98');
    for (const s of [-1, 1]) {
      const lx = s < 0 ? dx - 6 - (6 - dw) : dx + (6 - dw);
      if (dw <= 0.2) continue;
      vgrad(c, lx, yWin - 1, 6, by - 8 - yWin + 1, '#e8ebee', '#c4c9ce');
      rect(c, '#1c2630', lx + 1, yWin + 1, 4, hWin - 1);
      rect(c, 'rgba(160,210,255,0.3)', lx + 1.5, yWin + 1.5, 1, hWin - 2);
      rect(c, st.stripe, lx, yStripe, 6, 2.2);
    }
    rect(c, 'rgba(0,0,0,0.4)', dx - 0.3, yWin - 1, 0.6, by - 8 - yWin + 1);
    // Door-status lamp above each door, blinking while they move.
    ellipse(c, open > 0.02 && open < 0.98 && lamp ? '#ffb43a' : open >= 0.98 ? '#ff8a3a' : '#7a5a3a', dx, yWin - 3.5, 1, 0.8);
  }
  // Cabs.
  const cab = (x: number, dir: number) => {
    const nx = dir > 0 ? x1 : x0;
    // Sloped windscreen.
    poly(c, '#141c24', [nx - dir * 14, yWin - 3, nx - dir * 5, yWin - 3, nx - dir * 1.2, yWin + hWin, nx - dir * 14, yWin + hWin]);
    poly(c, 'rgba(160,210,255,0.3)', [nx - dir * 11, yWin - 2, nx - dir * 9, yWin - 2, nx - dir * 6, yWin + hWin - 1, nx - dir * 8, yWin + hWin - 1]);
    // Destination LED.
    rect(c, '#0a0c0e', nx - dir * 13 - (dir > 0 ? 0 : 7), top + 3, 7, 2.6);
    const scroll = Math.floor(t * 3) % 3;
    for (let i = 0; i < 3; i++) rect(c, i === scroll ? '#ffb43a' : '#a8641a', nx - dir * 13 - (dir > 0 ? 0 : 7) + 1 + i * 2, top + 3.8, 1.2, 1);
    // Headlight and tail light.
    ellipse(c, '#fff8d8', nx - dir * 3, yStripe + 4, 1.4, 1);
    ellipse(c, 'rgba(255,248,200,0.35)', nx - dir * 1, yStripe + 4, 4, 2.4);
    ellipse(c, '#e2493b', nx - dir * 6.5, yStripe + 4, 0.9, 0.8);
    void x;
  };
  if (cabL) cab(x0, -1);
  if (cabR) cab(x1, 1);
  if (label) {
    rect(c, '#0a0c0e', x0 + st.car / 2 - 12, yStripe + 3.5, 24, 3);
    txt(c, label, x0 + st.car / 2, yStripe + 5.1, 2.8, '#ffb43a', 700);
  }
  // Lower skirt shadow.
  rect(c, 'rgba(0,0,0,0.18)', x0 + 1, by - 9, x1 - x0 - 2, 2);
}

/** Screen-door centres in the MRT (shared by the doors, the floor markers and the train). */
const SD_FIRST = 40, SD_STEP = 48;

// ---------------------------------------------------------------------------
// Museum treasures
// ---------------------------------------------------------------------------

/** Plinth with a spot-lit glass case on top; `inner` draws the treasure standing on (cx, floor). */
function glassCase(c: Ctx, r: Rect, inner: (cx: number, floor: number) => void, caseH = 26): void {
  const { x, y, w, h } = r;
  const cx = x + w / 2;
  shadow(c, cx, y + h, w / 2 + 2, 3.5, 0.35);
  // Plinth.
  block(c, x + 2, y - 8, w - 4, h + 8, '#3a2a22', '#55402f', '#22160f');
  rect(c, '#d9b450', x + 3, y - 7, w - 6, 0.7);
  rect(c, 'rgba(255,255,255,0.08)', x + 4, y - 4, w - 8, h + 2);
  // Brass label plate.
  block(c, cx - 7, y, 14, 4, '#c9a24a', '#ecd28a');
  rect(c, '#6a5020', cx - 5, y + 1.6, 10, 0.5);
  // Case interior.
  const gx = x + 3, gy = y - 8 - caseH, gw = w - 6;
  vgrad(c, gx, gy, gw, caseH, '#3a2f2a', '#5a4638');
  // Silk-lined base and spot light pool.
  ellipse(c, 'rgba(255,230,170,0.35)', cx, y - 10, gw / 2 - 2, 3);
  rect(c, '#e8dcc0', gx, y - 11, gw, 3);
  rect(c, '#c8b898', gx, y - 9, gw, 1);
  const g = c.createRadialGradient(cx, gy + 4, 1, cx, gy + 10, caseH);
  g.addColorStop(0, 'rgba(255,240,200,0.4)');
  g.addColorStop(1, 'rgba(255,240,200,0)');
  c.fillStyle = g;
  c.fillRect(gx, gy, gw, caseH);
  inner(cx, y - 10);
  // Glass: edges, a faint tint and diagonal glints.
  rect(c, 'rgba(200,230,255,0.10)', gx, gy, gw, caseH);
  rect(c, 'rgba(230,245,255,0.7)', gx, gy, 0.6, caseH - 1);
  rect(c, 'rgba(230,245,255,0.7)', gx + gw - 0.6, gy, 0.6, caseH - 1);
  c.fillStyle = 'rgba(255,255,255,0.16)';
  c.beginPath();
  c.moveTo(gx + 2, gy + caseH - 3);
  c.lineTo(gx + 8, gy + 1);
  c.lineTo(gx + 11, gy + 1);
  c.lineTo(gx + 5, gy + caseH - 3);
  c.fill();
  rect(c, 'rgba(255,255,255,0.1)', gx + gw - 7, gy + 2, 2, caseH - 6);
  // Lid with its little lamp strip.
  block(c, gx - 1, gy - 3, gw + 2, 3.5, '#2a1e18', '#4a382c');
  rect(c, '#fff0c0', gx + 2, gy + 0.4, gw - 4, 0.5);
}

function cabbage(c: Ctx, cx: number, fy: number): void {
  // Small rosewood stand.
  block(c, cx - 5, fy - 2, 10, 2.5, '#5a2a1a', '#7a3e26');
  // The stalk: milky white jade fading into emerald leaves.
  const g = c.createLinearGradient(0, fy - 2, 0, fy - 20);
  g.addColorStop(0, '#f2f4e8');
  g.addColorStop(0.45, '#d8ecc8');
  g.addColorStop(0.7, '#5fbf6a');
  g.addColorStop(1, '#1f8a4a');
  c.fillStyle = g;
  c.beginPath();
  c.moveTo(cx - 2.6, fy - 2);
  c.quadraticCurveTo(cx - 4.2, fy - 10, cx - 3.4, fy - 17);
  c.quadraticCurveTo(cx - 1, fy - 21.5, cx + 1.5, fy - 20);
  c.quadraticCurveTo(cx + 4.4, fy - 16, cx + 3, fy - 9);
  c.quadraticCurveTo(cx + 2.6, fy - 4, cx + 2.4, fy - 2);
  c.closePath();
  c.fill();
  // Leaf ribs and the curling leaf edges.
  line(c, 'rgba(255,255,255,0.55)', 0.5, [cx - 0.5, fy - 3, cx - 1, fy - 12]);
  line(c, 'rgba(255,255,255,0.4)', 0.4, [cx + 1.2, fy - 3, cx + 1.6, fy - 11]);
  ellipse(c, '#2a9a55', cx - 2.8, fy - 15, 1.6, 3);
  ellipse(c, '#3aaa60', cx + 2.6, fy - 13.5, 1.5, 3.2);
  ellipse(c, '#7ad08a', cx - 0.8, fy - 18.5, 2, 1.4);
  ellipse(c, 'rgba(255,255,255,0.45)', cx - 1.8, fy - 8, 0.6, 2);
  // The katydid and the locust hiding on the leaves.
  ellipse(c, '#1a5a2a', cx + 1.2, fy - 19.6, 1.2, 0.5);
  line(c, '#1a5a2a', 0.3, [cx + 2.2, fy - 19.8, cx + 3.6, fy - 21.6]);
  line(c, '#1a5a2a', 0.3, [cx + 0.6, fy - 19.4, cx - 0.2, fy - 18.4]);
  ellipse(c, '#2a6a30', cx + 3.2, fy - 15, 0.4, 1);
}

function meatStone(c: Ctx, cx: number, fy: number): void {
  // Ornate gold stand with little feet.
  block(c, cx - 7, fy - 3, 14, 3, '#c9a24a', '#f0d890', '#8a6a20');
  for (let i = -5; i <= 5; i += 2.5) ellipse(c, '#8a6a20', cx + i, fy - 1.4, 0.5, 0.5);
  // A lumpy slab of braised pork belly: glazed skin on top, fat and meat layers down the side.
  const top = fy - 14;
  const outline = () => {
    c.beginPath();
    c.moveTo(cx - 6.5, fy - 3);
    c.quadraticCurveTo(cx - 7.6, top + 6, cx - 5.6, top + 2);
    c.quadraticCurveTo(cx - 3, top - 0.6, cx, top + 0.6);
    c.quadraticCurveTo(cx + 3.4, top - 1, cx + 6, top + 1.6);
    c.quadraticCurveTo(cx + 7.8, top + 6, cx + 6.4, fy - 3);
    c.closePath();
  };
  c.save();
  outline();
  c.clip();
  const bands = [['#4a200e', 3.2], ['#f4e0bc', 1.6], ['#b85e36', 1.6], ['#f6e6c8', 1], ['#a04e2c', 1.8], ['#ecd0a8', 1.2], ['#b8683e', 2]] as const;
  let yy = top - 1;
  for (const [col, hh] of bands) {
    c.fillStyle = col;
    c.beginPath();
    c.moveTo(cx - 9, yy + 0.6);
    c.quadraticCurveTo(cx - 2, yy - 0.5, cx + 9, yy + 0.4);
    c.lineTo(cx + 9, yy + hh + 0.6);
    c.quadraticCurveTo(cx - 2, yy + hh - 0.4, cx - 9, yy + hh + 0.8);
    c.fill();
    yy += hh;
  }
  // Glossy skin highlight and pores.
  ellipse(c, 'rgba(255,200,150,0.35)', cx - 2, top + 1.4, 3, 0.7);
  for (let i = 0; i < 7; i++) rect(c, '#1e0a04', cx - 5 + i * 1.6, top + 1.2 + ((i * 3) % 2) * 0.8, 0.45, 0.45);
  rect(c, 'rgba(0,0,0,0.2)', cx + 3.5, top, 4, 14);
  c.restore();
}

function ding(c: Ctx, cx: number, fy: number): void {
  const bronze = '#7a6a3a', patina = '#5f8a6a';
  // Three legs.
  for (const dx of [-5, 0, 5]) {
    block(c, cx + dx - 1, fy - 6, 2, 6, dx ? bronze : shade(bronze, -0.2));
    ellipse(c, shade(bronze, -0.3), cx + dx, fy - 0.3, 1.3, 0.5);
  }
  // Round belly with a cast band of motifs.
  c.fillStyle = bronze;
  c.beginPath();
  c.moveTo(cx - 7.5, fy - 15);
  c.lineTo(cx + 7.5, fy - 15);
  c.quadraticCurveTo(cx + 8, fy - 5, cx, fy - 5);
  c.quadraticCurveTo(cx - 8, fy - 5, cx - 7.5, fy - 15);
  c.fill();
  ellipse(c, 'rgba(95,138,106,0.75)', cx + 2, fy - 9, 4, 2.6);
  ellipse(c, 'rgba(95,138,106,0.5)', cx - 4, fy - 11, 2, 1.6);
  rect(c, shade(bronze, -0.35), cx - 7.2, fy - 13.6, 14.4, 1.6);
  for (let i = -6; i <= 5; i += 2.4) rect(c, '#b8a060', cx + i, fy - 13.2, 1, 0.7);
  ellipse(c, 'rgba(255,240,200,0.35)', cx - 4, fy - 9, 0.9, 2.4);
  // Rim and two upright ears.
  ellipse(c, shade(bronze, 0.15), cx, fy - 15, 7.8, 1.4);
  ellipse(c, '#2a2216', cx, fy - 15, 6.4, 0.9);
  for (const s of [-1, 1]) {
    c.strokeStyle = shade(bronze, 0.1);
    c.lineWidth = 1.1;
    c.beginPath();
    c.moveTo(cx + s * 5.5, fy - 15);
    c.lineTo(cx + s * 5.5, fy - 19.5);
    c.lineTo(cx + s * 3.2, fy - 19.5);
    c.lineTo(cx + s * 3.2, fy - 15.5);
    c.stroke();
  }
  ellipse(c, patina, cx + 5.5, fy - 17.5, 0.5, 0.8);
}

function ruBasin(c: Ctx, cx: number, fy: number): void {
  const glaze = '#a8d4d0';
  // Four cloud-shaped feet.
  for (const dx of [-6, -2, 2, 6]) ellipse(c, shade(glaze, -0.2), cx + dx, fy - 1, 1.2, 1);
  // Oval basin seen a little from above: sky-blue-after-rain glaze with crackle.
  c.fillStyle = shade(glaze, -0.12);
  c.beginPath();
  c.moveTo(cx - 9, fy - 6);
  c.lineTo(cx + 9, fy - 6);
  c.quadraticCurveTo(cx + 9, fy - 1.5, cx, fy - 1.5);
  c.quadraticCurveTo(cx - 9, fy - 1.5, cx - 9, fy - 6);
  c.fill();
  ellipse(c, glaze, cx, fy - 6, 9, 2.6);
  ellipse(c, shade(glaze, 0.25), cx, fy - 6.1, 7.8, 1.8);
  ellipse(c, 'rgba(255,255,255,0.5)', cx - 4, fy - 4, 2, 0.5);
  c.strokeStyle = 'rgba(70,110,110,0.35)';
  c.lineWidth = 0.3;
  c.beginPath();
  for (const [a, b, d, e] of [[-6, -5, -3, -2.5], [-2, -5.6, 1, -3], [3, -5, 6, -2.5], [1, -3, 4, -2], [-7, -3.5, -4, -2]]) {
    c.moveTo(cx + a, fy + b);
    c.lineTo(cx + d, fy + e);
  }
  c.stroke();
  // Little stand card.
  rect(c, '#f4ead4', cx + 6, fy - 2, 3, 2);
}

/** A tiny ink-wash landscape inside (x, y, w, h): misty peaks, a river, a bridge, pines and a boat. */
function inkLandscape(c: Ctx, x: number, y: number, w: number, h: number, seed: number): void {
  vgrad(c, x, y, w, h, '#efe2c2', '#e2d0a8');
  c.save();
  c.beginPath();
  c.rect(x, y, w, h);
  c.clip();
  const base = y + h * 0.72;
  for (let layer = 0; layer < 2; layer++) {
    c.fillStyle = layer ? 'rgba(40,46,40,0.55)' : 'rgba(90,110,100,0.35)';
    c.beginPath();
    c.moveTo(x, base);
    for (let i = 0; i <= w; i += 2) {
      const k = i / w;
      const hh = (Math.sin(k * 9 + seed + layer * 2) * 0.5 + 0.5) * (Math.sin(k * 3.3 + seed * 2) * 0.3 + 0.7);
      c.lineTo(x + i, base - hh * h * (layer ? 0.45 : 0.62) - (layer ? 0 : 1));
    }
    c.lineTo(x + w, base);
    c.fill();
  }
  // Mist and river.
  rect(c, 'rgba(239,226,194,0.7)', x, base - 2, w, 1.4);
  rect(c, 'rgba(120,150,150,0.45)', x, base + 1, w, 1);
  // Pines, a hut, a bridge and a boat.
  for (let i = 4; i < w - 2; i += 13) {
    rect(c, '#3a3028', x + i, base - 3, 0.5, 3);
    ellipse(c, 'rgba(40,60,40,0.8)', x + i + 0.2, base - 3.5, 1.3, 0.7);
  }
  if (w > 40) {
    line(c, '#5a4a3a', 0.4, [x + w * 0.45, base + 1.6, x + w * 0.5, base + 0.4, x + w * 0.55, base + 1.6]);
    rect(c, '#7a2a20', x + w * 0.2, base - 1.2, 1.4, 1);
    ellipse(c, '#3a3028', x + w * 0.72, base + 1.4, 1.4, 0.4);
  }
  c.restore();
}

// ---------------------------------------------------------------------------
// Interactive objects
// ---------------------------------------------------------------------------

function entrance(c: Ctx, r: Rect, name: string, en: string, t: number, roof: string): void {
  // A station entrance standing on the pavement in front of the facade.
  const { x, y, w } = r;
  const by = y + r.h, cx = x + w / 2;
  shadow(c, cx, by, w / 2 + 4, 3);
  // Portal frame (granite pillars + lintel).
  block(c, x - 4, by - 40, w + 8, 40, '#d8d2c4', '#ece6d8', '#a8a294');
  // Glass doors.
  vgrad(c, x + 2, by - 28, w - 4, 28, '#3a5a70', '#1c2c38');
  for (let i = 0; i < 3; i++) {
    const dx = x + 3 + i * ((w - 6) / 3);
    rect(c, '#9aa6ae', dx, by - 28, 0.8, 28);
    rect(c, 'rgba(180,220,255,0.25)', dx + 2, by - 27, 2, 20);
    rect(c, '#c8d0d6', dx + (w - 6) / 6 - 0.4, by - 16, 0.8, 5);
  }
  rect(c, '#9aa6ae', x + w - 3.8, by - 28, 0.8, 28);
  // Warm interior light.
  rect(c, 'rgba(255,230,160,0.18)', x + 2, by - 12, w - 4, 12);
  // Square pillars.
  for (const px of [x - 4, x + w / 2 - 2, x + w]) block(c, px, by - 40, 4, 40, '#c4bcaa', '#e0d8c6', '#948c7a');
  // Overhanging traditional eave with upturned corners.
  poly(c, shade(roof, -0.45), [x - 12, by - 40, x + w + 12, by - 40, x + w + 8, by - 46, x - 8, by - 46]);
  vgrad(c, x - 9, by - 47, w + 18, 6, shade(roof, 0.15), shade(roof, -0.15));
  for (let i = x - 8; i < x + w + 8; i += 3) rect(c, 'rgba(0,0,0,0.2)', i, by - 46, 0.5, 5);
  poly(c, roof, [x - 12, by - 41, x - 9, by - 47, x - 14, by - 46]);
  poly(c, roof, [x + w + 12, by - 41, x + w + 9, by - 47, x + w + 14, by - 46]);
  // Name board.
  block(c, cx - 22, by - 39, 44, 10, '#2a1810', '#4a3020');
  txt(c, name, cx, by - 34.6, 7.2, '#ffd34e', 900);
  rect(c, '#d9b450', cx - 21, by - 38.6, 42, 0.5);
  txt(c, en, cx, by - 30.4, 2.6, '#fff3d0', 700);
  if (isNight()) ellipse(c, 'rgba(255,220,120,0.18)', cx, by - 34, 26, 7);
  void t;
}

export const OBJ4: Partial<Record<ObjectId, Drawer>> = {
  // --- Town station ---------------------------------------------------------------
  stTicket(c, r) {
    const { x, y, w, h } = r;
    const by = y + h, cx = x + w / 2;
    shadow(c, cx, by, w / 2 + 2, 3);
    // Booth wall with two clerk windows.
    block(c, x, by - 34, w, 34, '#eef2f4', '#ffffff', '#c8d0d6');
    rect(c, '#2f6fa8', x + 1, by - 34, w - 2, 3);
    for (let i = 0; i < 2; i++) {
      const wx = x + 5 + i * (w / 2), ww = w / 2 - 10;
      block(c, wx - 1, by - 27, ww + 2, 15, '#8a96a0');
      vgrad(c, wx, by - 26, ww, 13, '#cfe6f2', '#9cc4da');
      // The clerk: cap, face and uniform.
      const kx = wx + ww / 2 + (i ? 1 : -1);
      rect(c, '#2a4a7a', kx - 4, by - 18, 8, 5);
      rect(c, '#ffffff', kx - 1, by - 18, 2, 3);
      ellipse(c, '#f2c8a0', kx, by - 20.5, 2.6, 2.8);
      rect(c, '#1e2f50', kx - 3, by - 24, 6, 1.6);
      rect(c, '#1e2f50', kx - 2.2, by - 25, 4.4, 1.2);
      rect(c, '#ffd34e', kx - 0.5, by - 24.4, 1, 0.8);
      rect(c, '#2a1810', kx - 1.4, by - 21, 0.6, 0.6);
      rect(c, '#2a1810', kx + 0.8, by - 21, 0.6, 0.6);
      // Speaking grille and glare.
      for (let k = 0; k < 3; k++) rect(c, 'rgba(60,80,100,0.6)', kx - 1.5 + k * 1.3, by - 15.5, 0.6, 0.6);
      rect(c, 'rgba(255,255,255,0.45)', wx + 1, by - 25, 1, 11);
      rect(c, 'rgba(255,255,255,0.25)', wx + ww - 4, by - 25, 2, 6);
    }
    // Counter top and front.
    block(c, x - 1, by - 12, w + 2, 4, '#c9a06a', '#e2bd88', '#8a6a40');
    block(c, x, by - 8, w, 8, '#2f6fa8', '#4a8cc8', '#1f4f80');
    rect(c, '#ffffff', x + 2, by - 5, w - 4, 0.6);
    // Little tray and a ticket.
    rect(c, '#9aa0a6', x + w / 4 - 3, by - 12.5, 6, 1);
    rect(c, '#ffd34e', x + w * 0.75 - 2, by - 13, 4, 1.4);
    // 售票 sign hanging above.
    rect(c, '#55606a', cx - 10, by - 41, 0.6, 4);
    rect(c, '#55606a', cx + 10, by - 41, 0.6, 4);
    block(c, cx - 15, by - 45, 30, 10, '#1f4f80', '#3a6fa8');
    txt(c, '售票', cx - 4, by - 40.2, 6.4, '#ffffff', 900);
    txt(c, 'Tickets', cx + 8.5, by - 40, 2.6, '#cfe6f2', 700);
  },
  stTrain(c, r, t) {
    // A modern EMU waiting at the platform; it breathes a little and blinks its lights.
    const by = r.y + r.h + 34;
    const bob = Math.sin(t * 1.3) * 0.25;
    rect(c, 'rgba(0,0,0,0.25)', 4, by - 2, 312, 3);
    c.save();
    c.translate(0, bob);
    train(c, EMU, 4, 316, by, t, 24, 0, true, true, '區間 臺北');
    c.restore();
  },

  // --- Taipei ---------------------------------------------------------------------
  twElevator(c, r, t) {
    const { x, y, w } = r;
    const by = y + r.h, cx = x + w / 2;
    // Stone entrance frame on the glass podium.
    block(c, x - 4, by - 30, w + 8, 30, '#8a8f94', '#b8bdc2', '#5a5f64');
    vgrad(c, x, by - 24, w, 24, '#25384a', '#121c26');
    // Two pairs of sliding glass doors.
    for (let i = 0; i < 4; i++) {
      const dx = x + 1 + i * ((w - 2) / 4);
      rect(c, '#c8cdd2', dx, by - 24, 0.8, 24);
      rect(c, 'rgba(170,215,255,0.22)', dx + 2, by - 22, 2.5, 16);
      rect(c, '#e8ecef', dx + (w - 2) / 4 - 1.6, by - 13, 0.6, 5);
    }
    rect(c, 'rgba(255,225,150,0.2)', x, by - 10, w, 10);
    // Canopy.
    block(c, x - 8, by - 34, w + 16, 5, '#d8dcdf', '#f4f6f7', '#9aa0a6');
    rect(c, 'rgba(0,0,0,0.2)', x - 6, by - 29, w + 12, 1.5);
    // 觀景台 sign with a little up arrow.
    block(c, cx - 16, by - 44, 32, 10, '#1f6f6a', '#3a9a90');
    txt(c, '觀景台', cx - 3, by - 39, 6.5, '#ffffff', 900);
    poly(c, '#ffd34e', [cx + 10, by - 42, cx + 13.5, by - 38.5, cx + 6.5, by - 38.5]);
    rect(c, '#ffd34e', cx + 9, by - 38.5, 2, 2.5);
    const blink = Math.sin(t * 3) > 0;
    ellipse(c, blink ? '#7cffb0' : '#3a8a60', cx + 16, by - 32, 0.8, 0.8);
  },
  tpTrain: (c, r, t) => entrance(c, r, '臺北車站', 'TAIPEI MAIN STATION', t, '#4a6a5a'),
  tpNoodles: (c, r, t) =>
    stall(c, r, t, '牛肉麵', '#c0392b', (c, r, t) => {
      // A big pot of broth and two steaming bowls of beef noodles.
      block(c, r.x + 4, r.y - 19, 16, 10, '#9aa0a6', '#c8cdd2', '#6a7076');
      ellipse(c, '#8a3a1a', r.x + 12, r.y - 19, 7, 1.6);
      steam(c, r.x + 12, r.y - 21, t);
      for (let i = 0; i < 2; i++) {
        const bx = r.x + 30 + i * 14;
        ellipse(c, '#f4f1ea', bx, r.y - 11, 5, 2);
        ellipse(c, '#a8461e', bx, r.y - 11.6, 4, 1.3);
        for (let k = 0; k < 3; k++) rect(c, '#f2d38a', bx - 3 + k * 2, r.y - 12, 1.4, 0.5);
        block(c, bx + 0.5, r.y - 13, 2.5, 1.6, '#6a2a14');
        rect(c, '#57c278', bx - 2, r.y - 12.5, 1, 0.6);
        steam(c, bx, r.y - 13, t + i);
      }
    }),
  tpCakes: (c, r, t) =>
    stall(c, r, t, '鳳梨酥', '#e2a03a', (c, r) => {
      // Gift boxes and golden pineapple cakes on a tray, a pineapple on top.
      block(c, r.x + 3, r.y - 16, 16, 7, '#c0392b', '#e05a48');
      rect(c, '#ffd34e', r.x + 10, r.y - 16, 2, 7);
      block(c, r.x + 5, r.y - 21, 12, 5, '#ffd34e', '#fff0a0');
      rect(c, '#c0392b', r.x + 10, r.y - 21, 2, 5);
      for (let i = 0; i < 6; i++) block(c, r.x + 22 + (i % 3) * 5.5, r.y - 13 - Math.floor(i / 3) * 3.5, 5, 3.5, '#e8b04a', '#f8d070', '#b07a2a');
      ellipse(c, '#e8a020', r.x + r.w - 6, r.y - 12, 3, 3.6);
      for (let k = 0; k < 3; k++) rect(c, '#b07010', r.x + r.w - 8 + k * 1.5, r.y - 14 + k, 0.5, 0.5);
      poly(c, '#4a9a3a', [r.x + r.w - 6, r.y - 15.5, r.x + r.w - 9, r.y - 20, r.x + r.w - 6, r.y - 17.5, r.x + r.w - 3, r.y - 20]);
    }),
  ubike(c, r) {
    const { x, y, w } = r;
    const by = y + r.h;
    shadow(c, x + w / 2, by, w / 2 + 6, 3);
    // Docking rail.
    rect(c, '#55606a', x - 4, by - 3, w + 8, 1.5);
    // Kiosk.
    block(c, x - 8, by - 22, 7, 22, '#f4f1ea', '#ffffff', '#c8c0b0');
    rect(c, '#ffb43a', x - 7.5, by - 21.5, 6, 3);
    block(c, x - 7, by - 16, 5, 4, '#1b2a36');
    rect(c, '#5fc0e8', x - 6.4, by - 15.4, 3.8, 2.4);
    ellipse(c, '#ffd34e', x - 4.5, by - 9, 1.2, 1.2);
    // Three parked bikes, each a little lower and further right.
    for (let i = 0; i < 3; i++) {
      const bx = x + 8 + i * 13, byy = by - 2 + i * 0.8 - 2;
      rect(c, '#6a7078', bx + 7, byy - 8, 1.6, 8);
      sideBike(c, bx, byy, 1, i * 0.3);
    }
    txt(c, 'YouBike', x - 4.5, by - 24.5, 2.6, '#c06a10', 900);
  },

  // --- MRT ------------------------------------------------------------------------
  mrtMap(c, r) {
    const { x, y, w } = r;
    const by = y + r.h, cx = x + w / 2;
    shadow(c, cx, by, w / 2, 2.5);
    rect(c, '#8a9096', x + 4, by - 8, 2, 8);
    rect(c, '#8a9096', x + w - 6, by - 8, 2, 8);
    const bx = x - 2, bt = 3, bw = w + 4, bh = by - 8 - bt;
    block(c, bx, bt, bw, bh, '#d8dcdf', '#f4f6f7', '#9aa0a6');
    rect(c, '#ffffff', bx + 1.5, bt + 4, bw - 3, bh - 5.5);
    rect(c, '#1f4f80', bx + 1, bt + 1, bw - 2, 3);
    txt(c, '捷運路網圖', cx, bt + 2.6, 2.8, '#ffffff', 900);
    // Coloured lines with stations and white interchange rings.
    const L = bx + 4, R = bx + bw - 4, Tp = bt + 7, B = bt + bh - 3;
    const midX = (L + R) / 2, midY = (Tp + B) / 2;
    c.lineCap = 'round';
    c.lineJoin = 'round';
    const lines: [string, number[]][] = [
      ['#e3002c', [midX - 6, Tp, midX - 6, midY, midX + 2, B]],
      ['#0070bd', [L, midY + 1, R, midY + 1]],
      ['#008659', [midX - 4, B, midX + 1, midY - 3, R - 6, Tp]],
      ['#f8b61c', [L + 4, Tp + 1, midX - 6, midY, midX + 8, midY + 4, R - 2, B]],
      ['#c48c31', [L, Tp + 4, midX - 10, Tp + 4, midX + 4, Tp + 8, R, Tp + 8]],
    ];
    for (const [col, pts] of lines) line(c, col, 1.3, pts);
    c.lineCap = 'butt';
    for (const [col, pts] of lines) {
      for (let i = 0; i < pts.length; i += 2) ellipse(c, '#ffffff', pts[i], pts[i + 1], 0.5, 0.5);
      void col;
    }
    for (const [ix, iy] of [[midX - 6, midY + 1], [midX + 1, midY - 3], [midX - 6, midY]]) {
      ellipse(c, '#2a2a2a', ix, iy, 1.3, 1.3);
      ellipse(c, '#ffffff', ix, iy, 0.8, 0.8);
    }
    // "You are here" star.
    ellipse(c, '#e2493b', midX + 4, midY + 1, 1.2, 1.2);
    rect(c, 'rgba(255,255,255,0.35)', bx + 2, bt + 5, 1, bh - 8);
  },
  mrtRules(c, r) {
    const { x, y, w } = r;
    const by = y + r.h, cx = x + w / 2;
    shadow(c, cx, by, w / 2, 2.5);
    rect(c, '#8a9096', cx - 1, by - 8, 2, 8);
    const bx = x - 1, bt = 3, bw = w + 2, bh = by - 8 - bt;
    block(c, bx, bt, bw, bh, '#d8dcdf', '#f4f6f7', '#9aa0a6');
    rect(c, '#fbfaf5', bx + 1.5, bt + 4, bw - 3, bh - 5.5);
    rect(c, '#2f8fa8', bx + 1, bt + 1, bw - 2, 3);
    txt(c, '乘車須知', cx, bt + 2.6, 2.8, '#ffffff', 900);
    // Four pictograms: no food, no drinks, give up your seat, stand on the right.
    const icons = 4, iw = (bw - 4) / 2;
    for (let i = 0; i < icons; i++) {
      const ix = bx + 2 + (i % 2) * iw + iw / 2, iy = bt + 9 + Math.floor(i / 2) * 9;
      const ban = i < 2;
      ellipse(c, ban ? '#e2493b' : '#2f8fa8', ix, iy, 3.6, 3.6);
      ellipse(c, '#ffffff', ix, iy, 2.9, 2.9);
      if (i === 0) {
        ellipse(c, '#c8862a', ix, iy - 0.4, 1.8, 0.8);
        rect(c, '#57c278', ix - 1.8, iy + 0.1, 3.6, 0.4);
        rect(c, '#c8862a', ix - 1.8, iy + 0.5, 3.6, 0.8);
      } else if (i === 1) {
        poly(c, '#3a8fd9', [ix - 1.2, iy - 1.8, ix + 1.2, iy - 1.8, ix + 0.8, iy + 1.8, ix - 0.8, iy + 1.8]);
        rect(c, '#2a2a2a', ix + 0.2, iy - 2.8, 0.4, 1.2);
      } else if (i === 2) {
        ellipse(c, '#2a2a2a', ix - 1, iy - 1.6, 0.6, 0.6);
        rect(c, '#2a2a2a', ix - 1.3, iy - 0.9, 0.8, 2);
        rect(c, '#2a2a2a', ix - 0.6, iy + 0.6, 1.6, 0.6);
        rect(c, '#2f8fa8', ix + 0.6, iy - 0.4, 1.4, 2.2);
      } else {
        line(c, '#2a2a2a', 0.5, [ix - 2, iy + 1.6, ix + 1.6, iy - 1.6]);
        ellipse(c, '#2a2a2a', ix + 1.2, iy, 0.5, 0.5);
        rect(c, '#2a2a2a', ix + 0.9, iy + 0.4, 0.6, 1.4);
      }
      if (ban) line(c, '#e2493b', 0.6, [ix - 2.1, iy - 2.1, ix + 2.1, iy + 2.1]);
    }
    rect(c, 'rgba(0,0,0,0.25)', bx + 3, bt + bh - 3, bw - 6, 0.5);
  },
  mrtRide(c, r) {
    // Queue markings in front of the screen doors: green arrows and yellow lines.
    for (let dx = SD_FIRST; dx < 410; dx += SD_STEP) {
      if (dx < r.x || dx > r.x + r.w) continue;
      const y = r.y + 1;
      rect(c, 'rgba(255,211,78,0.8)', dx - 11, y, 4, 7);
      rect(c, 'rgba(255,211,78,0.8)', dx + 7, y, 4, 7);
      poly(c, 'rgba(60,180,100,0.85)', [dx - 2, y, dx + 2, y, dx + 2, y + 4, dx + 4, y + 4, dx, y + 8, dx - 4, y + 4, dx - 2, y + 4]);
    }
  },

  // --- Palace Museum --------------------------------------------------------------
  muCabbage: (c, r) => glassCase(c, r, (cx, fy) => cabbage(c, cx, fy)),
  muMeat: (c, r) => glassCase(c, r, (cx, fy) => meatStone(c, cx, fy)),
  muDing: (c, r) => glassCase(c, r, (cx, fy) => ding(c, cx, fy)),
  muRuware: (c, r) => glassCase(c, r, (cx, fy) => ruBasin(c, cx, fy)),
  muScroll(c, r) {
    const { x, y, w, h } = r;
    const by = y + h, cx = x + w / 2;
    shadow(c, cx, by, w / 2 + 2, 3.5, 0.35);
    // A long, low table case.
    block(c, x, by - 12, w, 12, '#3a2a22', '#55402f', '#22160f');
    rect(c, '#d9b450', x + 1, by - 11, w - 2, 0.6);
    for (const lx of [x + 4, x + w - 7]) rect(c, '#22160f', lx, by - 2, 3, 2);
    // Sloped glass top showing the unrolled handscroll.
    const sx = x + 4, sy = by - 27, sw = w - 8, sh = 15;
    block(c, sx - 2, sy - 2, sw + 4, sh + 3, '#2a1e18');
    rect(c, '#e8dcc0', sx, sy, sw, sh);
    // Silk mounting borders and the painting.
    rect(c, '#7a8aa0', sx, sy + 1, sw, 1);
    rect(c, '#7a8aa0', sx, sy + sh - 2, sw, 1);
    inkLandscape(c, sx + 6, sy + 2.5, sw - 12, sh - 5, 3.1);
    // Seals and colophons.
    rect(c, '#c0392b', sx + sw - 9, sy + 3.5, 1.4, 1.4);
    rect(c, '#c0392b', sx + 10, sy + sh - 5, 1.2, 1.2);
    for (let i = 0; i < 4; i++) rect(c, 'rgba(40,30,20,0.6)', sx + sw - 5 + (i % 2), sy + 3 + i * 2, 0.5, 1.4);
    // Wooden rollers at both ends.
    for (const rx of [sx - 1, sx + sw - 1]) {
      block(c, rx - 1, sy - 0.5, 3, sh + 1, '#6a3a20', '#8a5530');
      ellipse(c, '#e8d8b0', rx + 0.5, sy - 0.5, 1.2, 0.8);
    }
    // Glass glints.
    rect(c, 'rgba(200,230,255,0.10)', sx, sy, sw, sh);
    c.fillStyle = 'rgba(255,255,255,0.18)';
    for (const gx of [sx + 14, sx + sw * 0.6]) {
      c.beginPath();
      c.moveTo(gx, sy + sh);
      c.lineTo(gx + 6, sy);
      c.lineTo(gx + 9, sy);
      c.lineTo(gx + 3, sy + sh);
      c.fill();
    }
    block(c, cx - 7, by - 8, 14, 4, '#c9a24a', '#ecd28a');
  },
  muCurio(c, r) {
    const { x, y, w, h } = r;
    const by = y + h, cx = x + w / 2;
    shadow(c, cx, by, w / 2 + 2, 3.5, 0.35);
    block(c, x + 2, by - 10, w - 4, 10, '#3a2a22', '#55402f', '#22160f');
    rect(c, '#d9b450', x + 3, by - 9, w - 6, 0.6);
    // Glass case.
    const gx = x + 4, gy = by - 36, gw = w - 8, gh = 26;
    vgrad(c, gx, gy, gw, gh, '#3a2f2a', '#5a4638');
    // The opened lacquer curio box with its lid propped up behind.
    const bx = cx - 14, bt = by - 24;
    block(c, bx + 2, bt - 9, 24, 9, '#7a1e18', '#a83a2a');
    rect(c, '#d9b450', bx + 4, bt - 7, 20, 0.5);
    rect(c, '#d9b450', bx + 4, bt - 3, 20, 0.5);
    block(c, bx, bt, 28, 12, '#5a1a12', '#8a2a1e');
    // Compartments with tiny treasures.
    const cells = [
      [1, 1, 8, 5], [10, 1, 7, 5], [18, 1, 9, 5],
      [1, 7, 6, 4], [8, 7, 11, 4], [20, 7, 7, 4],
    ];
    for (let i = 0; i < cells.length; i++) {
      const [cx0, cy0, cw, ch] = cells[i];
      const ex = bx + cx0, ey = bt + cy0;
      rect(c, '#f0e2c0', ex, ey, cw, ch);
      rect(c, 'rgba(0,0,0,0.18)', ex, ey, cw, 0.6);
      const mx = ex + cw / 2, my = ey + ch / 2;
      switch (i) {
        case 0: // jade bi disc
          ellipse(c, '#6ab88a', mx, my, 2.4, 2);
          ellipse(c, '#f0e2c0', mx, my, 0.8, 0.7);
          break;
        case 1: // tiny vase
          ellipse(c, '#3a6fb8', mx, my + 0.6, 1.6, 1.6);
          rect(c, '#3a6fb8', mx - 0.6, my - 2, 1.2, 1.4);
          rect(c, '#ffffff', mx - 0.6, my + 0.4, 1.2, 0.4);
          break;
        case 2: // rolled scroll
          rect(c, '#e8d8b0', mx - 3.5, my - 0.8, 7, 1.8);
          rect(c, '#6a3a20', mx - 4.2, my - 1, 0.8, 2.2);
          rect(c, '#6a3a20', mx + 3.4, my - 1, 0.8, 2.2);
          break;
        case 3: // snuff bottle
          ellipse(c, '#e2a03a', mx, my + 0.4, 1.2, 1.4);
          rect(c, '#c0392b', mx - 0.5, my - 1.6, 1, 0.8);
          break;
        case 4: // ivory ball and a little pagoda
          ellipse(c, '#f8f2e0', mx - 2.4, my, 1.5, 1.5);
          ellipse(c, '#c8b898', mx - 2.4, my, 0.6, 0.6);
          poly(c, '#b8862a', [mx + 2, my - 2, mx + 3.4, my + 1.6, mx + 0.6, my + 1.6]);
          break;
        default: // gold seal
          block(c, mx - 1.4, my - 1, 2.8, 2.4, '#d9b450');
          break;
      }
    }
    // Glass and lid.
    rect(c, 'rgba(200,230,255,0.10)', gx, gy, gw, gh);
    rect(c, 'rgba(230,245,255,0.7)', gx, gy, 0.6, gh);
    rect(c, 'rgba(230,245,255,0.7)', gx + gw - 0.6, gy, 0.6, gh);
    c.fillStyle = 'rgba(255,255,255,0.15)';
    c.beginPath();
    c.moveTo(gx + 3, gy + gh - 2);
    c.lineTo(gx + 10, gy + 1);
    c.lineTo(gx + 13, gy + 1);
    c.lineTo(gx + 6, gy + gh - 2);
    c.fill();
    block(c, gx - 1, gy - 3, gw + 2, 3.5, '#2a1e18', '#4a382c');
    block(c, cx - 7, by - 6, 14, 4, '#c9a24a', '#ecd28a');
  },
  muExit(c, r) {
    // Floor arrow and a free-standing exit / MRT sign.
    const cx = r.x + r.w / 2, by = r.y + r.h;
    poly(c, 'rgba(60,180,100,0.35)', [cx - 4, by - 9, cx + 4, by - 9, cx + 4, by - 4, cx + 7, by - 4, cx, by + 2, cx - 7, by - 4, cx - 4, by - 4]);
    shadow(c, cx + 16, by, 6, 1.8);
    rect(c, '#55606a', cx + 15, by - 24, 2, 24);
    ellipse(c, '#3a3e44', cx + 16, by - 0.5, 4, 1.2);
    block(c, cx + 2, by - 36, 28, 13, '#1f8a4a', '#3aaa60');
    // Running-man pictogram and the words.
    ellipse(c, '#ffffff', cx + 6.5, by - 33.4, 1, 1);
    line(c, '#ffffff', 1, [cx + 6, by - 32, cx + 5, by - 28.5, cx + 3.5, by - 26]);
    line(c, '#ffffff', 0.8, [cx + 5, by - 28.5, cx + 7, by - 26.5]);
    line(c, '#ffffff', 0.8, [cx + 3.5, by - 31, cx + 7.5, by - 30]);
    txt(c, '出口', cx + 17, by - 32.4, 5.4, '#ffffff', 900);
    txt(c, 'EXIT', cx + 17, by - 27.2, 3, '#e8ffe8', 700);
    block(c, cx + 4, by - 22, 24, 7, '#1f4f80', '#3a6fa8');
    metroLogo(c, cx + 8, by - 18.5, 2.4);
    txt(c, '捷運 →', cx + 18.5, by - 18.3, 3.6, '#ffffff', 900);
  },

  // --- Hualien --------------------------------------------------------------------
  hlHelmet(c, r) {
    const { x, y, w, h } = r;
    const by = y + h, cx = x + w / 2;
    shadow(c, cx, by, w / 2 + 3, 3);
    // Little wooden booth with a green roof.
    block(c, x, by - 30, w, 30, '#c8a070', '#e0bc8c', '#8a6a40');
    for (let i = x + 4; i < x + w - 2; i += 5) rect(c, 'rgba(90,60,30,0.25)', i, by - 29, 0.6, 28);
    // Shelf with rows of yellow helmets.
    rect(c, '#5a3a20', x + 3, by - 26, w - 6, 13);
    for (let row = 0; row < 2; row++) {
      rect(c, '#8a6a40', x + 3, by - 19.5 + row * 6.5, w - 6, 1);
      for (let i = 0; i < 6; i++) {
        const hx = x + 6.5 + i * ((w - 13) / 5), hy = by - 20 + row * 6.5;
        c.fillStyle = '#ffd34e';
        c.beginPath();
        c.ellipse(hx, hy, 2.6, 2.6, 0, Math.PI, 0);
        c.fill();
        rect(c, '#e0a820', hx - 3, hy - 0.2, 6, 0.8);
        rect(c, 'rgba(255,255,255,0.6)', hx - 1.4, hy - 2, 1, 0.6);
      }
    }
    // Counter with one helmet waiting.
    block(c, x - 2, by - 12, w + 4, 4, '#8a5530', '#a36a40', '#6b3f22');
    block(c, x, by - 8, w, 8, '#4a8a3a', '#5fa04a', '#2f6a2a');
    c.fillStyle = '#ffd34e';
    c.beginPath();
    c.ellipse(x + w - 9, by - 12, 3.4, 3.2, 0, Math.PI, 0);
    c.fill();
    rect(c, '#e0a820', x + w - 13, by - 12.5, 8, 0.8);
    // Roof and sign.
    poly(c, '#2f6a3a', [x - 5, by - 29, x + w + 5, by - 29, x + w - 1, by - 36, x + 1, by - 36]);
    rect(c, '#4a8a4a', x + 1, by - 36, w - 2, 1);
    block(c, cx - 14, by - 45, 28, 9, '#fbf8ef', '#ffffff');
    txt(c, '安全帽', cx + 2, by - 40.4, 5.6, '#2f6a3a', 900);
    c.fillStyle = '#ffd34e';
    c.beginPath();
    c.ellipse(cx - 10, by - 39.5, 2.2, 2.2, 0, Math.PI, 0);
    c.fill();
  },
  hlView(c, r) {
    const { x, y, w, h } = r;
    const by = y + h, cx = x + w / 2;
    shadow(c, cx, by, w / 2 + 4, 2.6);
    // Short railing behind.
    rect(c, '#6a4a2a', x - 6, by - 12, w + 12, 1.4);
    rect(c, '#6a4a2a', x - 6, by - 7, w + 12, 1);
    for (const px of [x - 6, x + w + 4.6]) rect(c, '#55361f', px, by - 13, 1.6, 13);
    // Pedestal.
    rect(c, '#3a6a8a', cx - 1.4, by - 14, 2.8, 14);
    ellipse(c, '#2a4a62', cx, by - 0.5, 4, 1.4);
    // Coin box.
    block(c, cx - 3, by - 13, 6, 5, '#4a8ab0', '#6aaad0');
    rect(c, '#1b2a36', cx - 0.8, by - 11.6, 1.6, 0.5);
    ellipse(c, '#ffd34e', cx + 1.8, by - 9.6, 0.7, 0.7);
    // Twin-barrel viewer head with eyepieces and hood.
    block(c, cx - 7, by - 21, 14, 8, '#4a8ab0', '#6aaad0', '#2a5a7a');
    ellipse(c, '#1b2a36', cx - 3.4, by - 17, 2.2, 2.2);
    ellipse(c, '#1b2a36', cx + 3.4, by - 17, 2.2, 2.2);
    ellipse(c, 'rgba(160,220,255,0.6)', cx - 4, by - 17.6, 0.8, 0.8);
    ellipse(c, 'rgba(160,220,255,0.6)', cx + 2.8, by - 17.6, 0.8, 0.8);
    block(c, cx - 6, by - 23, 12, 2.4, '#2a5a7a');
    rect(c, '#2a5a7a', cx - 8.5, by - 18.5, 1.6, 4);
    rect(c, '#2a5a7a', cx + 7, by - 18.5, 1.6, 4);
  },
  hlStones(c, r) {
    const cx = r.x + r.w / 2, by = r.y + r.h;
    cairn(c, cx, by, 1);
  },
  hlSea(c, r, t) {
    // A shell and a starfish where the foam reaches.
    const cx = r.x + r.w / 2, by = r.y + r.h;
    ellipse(c, '#f4e8d8', cx - 4, by - 2, 2.2, 1.6);
    for (let i = -1; i <= 1; i++) line(c, '#c8a888', 0.3, [cx - 4, by - 0.6, cx - 4 + i * 1.4, by - 3.2]);
    c.fillStyle = '#ff8a5a';
    c.beginPath();
    for (let i = 0; i < 10; i++) {
      const a = -Math.PI / 2 + (i * Math.PI) / 5, rr = i % 2 ? 0.9 : 2.4;
      c.lineTo(cx + 4 + Math.cos(a) * rr, by - 3 + Math.sin(a) * rr * 0.8);
    }
    c.fill();
    void t;
  },
  hlMochi: (c, r, t) =>
    stall(c, r, t, '麻糬', '#ff9fc0', (c, r) => {
      // Trays of round mochi: white, pink, green and peanut-dusted.
      const cols = ['#fbf8ef', '#ffc0d0', '#b8e0a0', '#f2d8a8'];
      for (let tr = 0; tr < 3; tr++) {
        const tx = r.x + 5 + tr * 18;
        block(c, tx, r.y - 12, 16, 3, '#c8a070', '#e0bc8c');
        for (let i = 0; i < 6; i++) {
          const col = cols[(tr + i) % cols.length];
          ellipse(c, col, tx + 3 + (i % 3) * 5, r.y - 13.5 - Math.floor(i / 3) * 2.6, 2.2, 1.8);
          ellipse(c, 'rgba(255,255,255,0.6)', tx + 2.4 + (i % 3) * 5, r.y - 14.4 - Math.floor(i / 3) * 2.6, 0.7, 0.4);
        }
      }
      // A stack of gift boxes.
      block(c, r.x + r.w - 10, r.y - 22, 8, 6, '#ff9fc0', '#ffc0d8');
      rect(c, '#c0392b', r.x + r.w - 6.5, r.y - 22, 1, 6);
    }),
  hlTrain: (c, r, t) => entrance(c, r, '花蓮車站', 'HUALIEN STATION', t, '#8a5a3a'),
};

/** Original little metro mark: a white ring cut by a wave, on blue. */
function metroLogo(c: Ctx, cx: number, cy: number, rr: number): void {
  ellipse(c, '#ffffff', cx, cy, rr, rr);
  ellipse(c, '#1f6fc0', cx, cy, rr * 0.62, rr * 0.62);
  c.strokeStyle = '#ffffff';
  c.lineWidth = rr * 0.32;
  c.beginPath();
  c.moveTo(cx - rr * 0.9, cy + rr * 0.15);
  c.quadraticCurveTo(cx - rr * 0.3, cy - rr * 0.6, cx, cy + rr * 0.05);
  c.quadraticCurveTo(cx + rr * 0.3, cy + rr * 0.6, cx + rr * 0.9, cy - rr * 0.15);
  c.stroke();
}

/** A balanced stack of Qixingtan's smooth pebbles. */
function cairn(c: Ctx, cx: number, by: number, v: number): void {
  const cols = ['#9a9a94', '#e0dcd2', '#4a4e52', '#b8c0b4', '#f2eee4', '#6a6e6a', '#8aa0a8'];
  shadow(c, cx + 1, by, 9 + v, 2.6, 0.4, 14);
  let y = by;
  const n = 5 + v;
  for (let i = 0; i < n; i++) {
    const rw = 7.5 - i * 1 + (i === 0 ? 0.5 : 0), rh = 2.6 - i * 0.18;
    const off = Math.sin(i * 2.3 + v) * 1;
    y -= rh * 1.6;
    const col = cols[(i + v) % cols.length];
    ellipse(c, '#2a2a2c', cx + off, y + 0.3, rw + 0.6, rh + 0.6);
    ellipse(c, shade(col, -0.3), cx + off, y + 0.4, rw, rh);
    ellipse(c, col, cx + off, y - 0.2, rw - 0.3, rh - 0.4);
    ellipse(c, 'rgba(255,255,255,0.45)', cx + off - rw * 0.35, y - rh * 0.45, rw * 0.3, rh * 0.25);
  }
  // A few loose pebbles around.
  for (const [dx, dy, col] of [[-11, -1, '#4a4e52'], [10, -0.6, '#f2eee4'], [-8, 1, '#b8c0b4']] as const) {
    ellipse(c, '#2a2a2c', cx + dx, by + dy, 2.2, 1.4);
    ellipse(c, col, cx + dx, by + dy - 0.2, 1.7, 1);
  }
}

/** Side-view YouBike, wheels on `by`, front towards `dir`. */
function sideBike(c: Ctx, cx: number, by: number, dir: number, phase: number): void {
  const fx = cx + dir * 7.5, rx = cx - dir * 7.5, wy = by - 4.4, R = 4.2;
  for (const wx of [rx, fx]) {
    c.strokeStyle = '#1b1d24';
    c.lineWidth = 1.2;
    c.beginPath();
    c.arc(wx, wy, R, 0, Math.PI * 2);
    c.stroke();
    c.strokeStyle = 'rgba(200,205,210,0.8)';
    c.lineWidth = 0.35;
    c.beginPath();
    for (let k = 0; k < 3; k++) {
      const a = phase * Math.PI * 2 + (k * Math.PI) / 3;
      c.moveTo(wx - Math.cos(a) * (R - 0.6), wy - Math.sin(a) * (R - 0.6));
      c.lineTo(wx + Math.cos(a) * (R - 0.6), wy + Math.sin(a) * (R - 0.6));
    }
    c.stroke();
    ellipse(c, '#c8cdd2', wx, wy, 0.7, 0.7);
  }
  // Step-through orange frame.
  const orange = '#f08a24';
  c.lineCap = 'round';
  line(c, orange, 1.4, [rx, wy, cx - dir * 1, wy - 0.5, fx - dir * 2, wy - 6.5]);
  line(c, orange, 1.4, [cx - dir * 1, wy - 0.5, cx - dir * 3.5, wy - 6]);
  line(c, orange, 1.1, [fx, wy, fx - dir * 2, wy - 6.5, fx - dir * 2.6, wy - 8.5]);
  line(c, '#ffd34e', 1, [rx, wy, cx - dir * 3.5, wy - 6]);
  c.lineCap = 'butt';
  // Chain guard, saddle, handlebar and the front basket.
  ellipse(c, '#f4f1ea', cx - dir * 2, wy - 0.2, 3, 1.2);
  block(c, cx - dir * 3.5 - 2.5, wy - 7.6, 5, 1.6, '#2a2a30');
  rect(c, '#2a2a30', fx - dir * 2.6 - 1.6, wy - 9, 3.2, 1);
  block(c, fx - (dir > 0 ? 0 : 5) - dir * 0.5, wy - 8.5, 5, 4, '#e8ecef', '#ffffff', '#a8aeb3');
  rect(c, 'rgba(90,100,110,0.5)', fx - (dir > 0 ? 0 : 5) - dir * 0.5 + 1, wy - 7, 3, 0.4);
  // Pedal turning with the wheels.
  const pa = phase * Math.PI * 2;
  rect(c, '#2a2a30', cx - dir * 1 + Math.cos(pa) * 1.8 - 0.8, wy - 0.5 + Math.sin(pa) * 1.8 - 0.3, 1.6, 0.6);
}

/**
 * The YouBike under the rider (feet/ground at x, y). `phase` is the 0..1 walk cycle.
 */
export function drawBike(c: Ctx, x: number, y: number, facing: 'up' | 'down' | 'left' | 'right', phase: number): void {
  shadow(c, x, y + (facing === 'left' || facing === 'right' ? 0 : 2), facing === 'left' || facing === 'right' ? 12 : 5, facing === 'left' || facing === 'right' ? 2.2 : 4);
  if (facing === 'left' || facing === 'right') {
    sideBike(c, x, y, facing === 'right' ? 1 : -1, phase * 2);
    return;
  }
  // Seen from behind (up) or the front (down): thin wheels, handlebar, basket in front.
  const front = facing === 'down' ? 1 : -1;
  const tread = (wy: number, h: number) => {
    block(c, x - 1.3, wy - h, 2.6, h * 2, '#1b1d24');
    for (let k = 0; k < 3; k++) {
      const yy = wy - h + (((phase * 4 + k / 3) % 1) * h * 2);
      rect(c, 'rgba(160,165,170,0.7)', x - 0.8, yy, 1.6, 0.5);
    }
  };
  if (front > 0) {
    // Riding towards the camera: wide handlebar behind the hands, basket and front wheel below the feet.
    tread(y - 7, 2.4);
    rect(c, '#f08a24', x - 0.9, y - 12, 1.8, 10);
    rect(c, '#2a2a30', x - 9, y - 12.6, 18, 1.3);
    block(c, x - 10.5, y - 13.2, 3, 2.4, '#2a2a30');
    block(c, x + 7.5, y - 13.2, 3, 2.4, '#2a2a30');
    tread(y + 3.6, 3.4);
    rect(c, '#f08a24', x - 2.6, y - 0.5, 5.2, 1.2);
    block(c, x - 5, y - 4, 10, 5, '#e8ecef', '#ffffff', '#a8aeb3');
    rect(c, 'rgba(90,100,110,0.5)', x - 4, y - 2, 8, 0.4);
    rect(c, '#f08a24', x - 4, y - 0.4, 8, 0.6);
    ellipse(c, '#fff3b0', x, y + 1.6, 1.2, 0.8);
  } else {
    // Riding away: handlebar ends poke out above, rear wheel with fender and a red reflector below.
    rect(c, '#2a2a30', x - 9, y - 15, 18, 1.3);
    block(c, x - 10.5, y - 15.6, 3, 2.4, '#2a2a30');
    block(c, x + 7.5, y - 15.6, 3, 2.4, '#2a2a30');
    tread(y + 2.6, 3.6);
    block(c, x - 1.8, y - 2.5, 3.6, 5, '#f08a24', '#ffb060');
    ellipse(c, '#e2493b', x, y + 0.4, 1.1, 0.8);
    rect(c, '#ffd34e', x - 4, y - 4, 8, 1);
  }
}

// ---------------------------------------------------------------------------
// Taipei 101
// ---------------------------------------------------------------------------

const GLASS_L = '#6fb4ac', GLASS_R = '#3a7f82', GLASS_EDGE = '#1d4446';

function tower101(c: Ctx, cx: number, by: number, night: boolean): void {
  const dayCol = ['#e2493b', '#ff9a3a', '#ffd34e', '#57c278', '#3a8fd9', '#5a5ad8', '#b06ad8'][new Date().getDay()];
  // Facade: a trapezoid split into a lit left face and a shaded right face.
  const seg = (y0: number, y1: number, wTop: number, wBot: number, lines: number) => {
    const sx = cx - 2;
    poly(c, GLASS_EDGE, [cx - wTop / 2 - 0.6, y0 - 0.4, cx + wTop / 2 + 0.6, y0 - 0.4, cx + wBot / 2 + 0.6, y1, cx - wBot / 2 - 0.6, y1]);
    const gl = c.createLinearGradient(0, y0, 0, y1);
    gl.addColorStop(0, shade(GLASS_L, 0.2));
    gl.addColorStop(1, shade(GLASS_L, -0.12));
    c.fillStyle = gl;
    c.beginPath();
    c.moveTo(cx - wTop / 2, y0);
    c.lineTo(sx, y0);
    c.lineTo(sx, y1);
    c.lineTo(cx - wBot / 2, y1);
    c.fill();
    const gr = c.createLinearGradient(0, y0, 0, y1);
    gr.addColorStop(0, shade(GLASS_R, 0.1));
    gr.addColorStop(1, shade(GLASS_R, -0.2));
    c.fillStyle = gr;
    c.beginPath();
    c.moveTo(sx, y0);
    c.lineTo(cx + wTop / 2, y0);
    c.lineTo(cx + wBot / 2, y1);
    c.lineTo(sx, y1);
    c.fill();
    // Floor lines and window lights.
    const h = y1 - y0;
    for (let k = 1; k < lines; k++) {
      const yy = y0 + (h * k) / lines;
      const f = k / lines, ww = wTop + (wBot - wTop) * f;
      rect(c, 'rgba(20,50,52,0.35)', cx - ww / 2 + 0.6, yy, ww - 1.2, 0.45);
      if (night) {
        for (let i = 0; i < ww - 3; i += 2.2) {
          if (((i * 7 + yy * 13) | 0) % 5 < 2) rect(c, 'rgba(255,226,150,0.85)', cx - ww / 2 + 1.4 + i, yy - 1.2, 1, 0.8);
        }
      }
    }
    // Vertical mullions.
    for (let i = -3; i <= 3; i++) {
      const xt = cx + (i * wTop) / 8, xb = cx + (i * wBot) / 8;
      line(c, 'rgba(20,50,52,0.25)', 0.35, [xt, y0, xb, y1]);
    }
    rect(c, 'rgba(255,255,255,0.35)', cx - wTop / 2 + 0.3, y0, 0.6, h * 0.8);
  };
  // Base (a tapering trunk) on the podium roof.
  const baseH = 44;
  shadow(c, cx, by, 22, 4, 0.3);
  seg(by - baseH, by, 26, 34, 12);
  // Giant coin ornaments on top of the base.
  const coinY = by - baseH + 6;
  for (const dx of [-7, 5]) {
    ellipse(c, '#9a7a2a', dx + cx, coinY, 4, 4);
    ellipse(c, '#e8c860', dx + cx, coinY - 0.3, 3.4, 3.4);
    rect(c, '#2a5a5a', dx + cx - 1.1, coinY - 1.4, 2.2, 2.2);
  }
  // Eight flared "bamboo" segments, each wider at its top.
  const sh = 17;
  let y = by - baseH;
  for (let i = 0; i < 8; i++) {
    const y1 = y, y0 = y - sh;
    seg(y0, y1, 34, 25, 6);
    // Ledge at the joint and the ruyi corner ornaments.
    rect(c, '#d8e4e2', cx - 17.5, y0 - 0.2, 35, 1);
    rect(c, 'rgba(0,0,0,0.25)', cx - 17, y0 + 0.8, 34, 0.5);
    if (night) rect(c, dayCol, cx - 17, y0 + 0.4, 34, 0.9);
    for (const s of [-1, 1]) {
      const rx = cx + s * 14.5, ry = y1 - 2.8;
      ellipse(c, '#e8c860', rx, ry, 1.5, 1.2);
      ellipse(c, '#e8c860', rx - s * 1.2, ry + 1.3, 0.9, 0.8);
      ellipse(c, '#9a7a2a', rx, ry, 0.5, 0.4);
    }
    y = y0;
  }
  // Ruyi motif on each face just above the base.
  for (const dx of [-6.5, 4.5]) {
    const rx = cx + dx, ry = by - baseH - 8;
    ellipse(c, '#e8c860', rx, ry, 2.4, 1.9);
    ellipse(c, '#e8c860', rx - 2, ry + 1.4, 1.3, 1.1);
    ellipse(c, '#e8c860', rx + 2, ry + 1.4, 1.3, 1.1);
    ellipse(c, '#9a7a2a', rx, ry + 0.2, 0.9, 0.7);
  }
  // Crown: a stepped cap and the needle-like pinnacle.
  seg(y - 7, y, 22, 26, 3);
  seg(y - 12, y - 7, 15, 18, 2);
  block(c, cx - 5, y - 17, 10, 5, '#c8d6d4', '#e8f0ee', '#8a9a98');
  block(c, cx - 2.5, y - 26, 5, 9, '#b8c6c4', '#e0e8e6');
  for (let k = 0; k < 3; k++) rect(c, '#8a9a98', cx - 2.5, y - 24 + k * 2.6, 5, 0.5);
  rect(c, '#9aa8a6', cx - 0.8, y - 44, 1.6, 18);
  rect(c, '#d8e2e0', cx - 0.8, y - 44, 0.6, 18);
  rect(c, '#9aa8a6', cx - 0.3, y - 52, 0.6, 8);
  ellipse(c, night ? '#ff5a4a' : '#e2493b', cx, y - 52, 0.8, 0.8);
  if (night) ellipse(c, 'rgba(255,90,70,0.3)', cx, y - 52, 3, 3);
}

// ---------------------------------------------------------------------------
// Decor
// ---------------------------------------------------------------------------

function trafficPhase(t: number, x: number): { green: boolean; left: number } {
  const p = (t + x * 0.01) % 24;
  return p < 12 ? { green: true, left: Math.ceil(12 - p) } : { green: false, left: Math.ceil(24 - p) };
}

function gate(c: Ctx, x: number, by: number, arrow: boolean): void {
  // A ticket-gate cabinet seen from the front: card pad on top, status arrow, flaps.
  block(c, x - 3, by - 15, 6, 15, '#c8cdd2', '#e8ecef', '#8a9096');
  rect(c, '#55606a', x - 2.5, by - 15, 5, 3.5);
  ellipse(c, '#3a8fd9', x, by - 13.3, 1.5, 1);
  ellipse(c, '#9fd6ff', x - 0.4, by - 13.6, 0.5, 0.3);
  block(c, x - 2, by - 10.5, 4, 3.5, '#101418');
  if (arrow) poly(c, '#5aff8a', [x, by - 10, x + 1.3, by - 8.6, x + 0.5, by - 8.6, x + 0.5, by - 7.4, x - 0.5, by - 7.4, x - 0.5, by - 8.6, x - 1.3, by - 8.6]);
  else {
    line(c, '#ff4a3a', 0.5, [x - 1, by - 9.8, x + 1, by - 7.6]);
    line(c, '#ff4a3a', 0.5, [x + 1, by - 9.8, x - 1, by - 7.6]);
  }
  rect(c, '#ffd34e', x - 3, by - 2, 6, 0.8);
}

export function drawDecor4(c: Ctx, d: Decor, t: number): boolean {
  const { x, y } = d;
  switch (d.kind) {
    case 'stationBench': {
      const w = d.w ?? 48;
      shadow(c, x, y, w / 2, 2.6);
      for (const lx of [x - w / 2 + 4, x + w / 2 - 6]) block(c, lx, y - 7, 2, 7, '#6a7078');
      // Perforated steel seats with a backrest.
      block(c, x - w / 2 + 1, y - 18, w - 2, 7, '#3a7fb0', '#5a9fd0', '#2a5f88');
      block(c, x - w / 2, y - 11, w, 4, '#3a7fb0', '#6aaee0', '#2a5f88');
      for (let i = x - w / 2 + 3; i < x + w / 2 - 2; i += 3) {
        rect(c, 'rgba(10,30,50,0.35)', i, y - 16, 0.8, 0.8);
        rect(c, 'rgba(10,30,50,0.35)', i + 1.5, y - 14, 0.8, 0.8);
      }
      for (let i = 1; i < 3; i++) rect(c, '#55606a', x - w / 2 + (i * w) / 3 - 0.6, y - 18, 1.2, 11);
      return true;
    }
    case 'ticketGates': {
      const w = d.w ?? 128;
      const n = Math.max(2, Math.round(w / 20) + 1);
      const step = w / (n - 1);
      shadow(c, x, y, w / 2 + 2, 2.4, 0.2);
      for (let i = 0; i < n; i++) {
        const gx = x - w / 2 + i * step;
        // Little swing flaps reaching into each passage.
        if (i < n - 1) {
          poly(c, 'rgba(226,90,60,0.75)', [gx + 3, y - 9, gx + 6.5, y - 8, gx + 6.5, y - 5, gx + 3, y - 4]);
          poly(c, 'rgba(226,90,60,0.75)', [gx + step - 3, y - 9, gx + step - 6.5, y - 8, gx + step - 6.5, y - 5, gx + step - 3, y - 4]);
        }
        gate(c, gx, y, i % 4 !== 3);
      }
      return true;
    }
    case 'stationArrow': {
      shadow(c, x, y, 5, 1.6);
      rect(c, '#55606a', x - 1, y - 30, 2, 30);
      ellipse(c, '#3a3e44', x, y - 0.5, 3, 1);
      // Arrow-shaped board pointing east.
      poly(c, '#163a5e', [x - 15, y - 41, x + 13, y - 41, x + 19, y - 34.5, x + 13, y - 28, x - 15, y - 28]);
      poly(c, '#1f5f9a', [x - 14, y - 40, x + 12.5, y - 40, x + 17.8, y - 34.5, x + 12.5, y - 29, x - 14, y - 29]);
      rect(c, 'rgba(255,255,255,0.25)', x - 13, y - 39.5, 25, 0.6);
      // Train pictogram.
      block(c, x - 12.5, y - 38.5, 8, 7, '#ffffff');
      rect(c, '#1f5f9a', x - 11.5, y - 37.2, 6, 2.2);
      ellipse(c, '#1f5f9a', x - 10.5, y - 33, 0.6, 0.6);
      ellipse(c, '#1f5f9a', x - 6.5, y - 33, 0.6, 0.6);
      line(c, '#ffffff', 0.5, [x - 11.5, y - 30, x - 10, y - 31.4]);
      line(c, '#ffffff', 0.5, [x - 5.5, y - 30, x - 7, y - 31.4]);
      txt(c, '車站', x + 2, y - 35.8, 6, '#ffffff', 900);
      txt(c, 'STATION', x + 2, y - 30.8, 2.4, '#cfe6f2', 700);
      poly(c, '#ffd34e', [x + 12, y - 37.5, x + 15.5, y - 34.5, x + 12, y - 31.5]);
      return true;
    }
    case 'tower101': {
      const night = isNight();
      cachedArt(c, `101:${night ? 'n' : 'd'}`, x - 30, y - 230, 60, 238, (cc) => tower101(cc, x, y, night));
      return true;
    }
    case 'sign101': {
      shadow(c, x, y, 28, 3);
      block(c, x - 26, y - 14, 52, 14, '#4a4e54', '#6a7078', '#2a2e33');
      rect(c, '#8a9096', x - 25, y - 13, 50, 0.6);
      txt(c, 'TAIPEI 101', x + 5, y - 9.4, 6, '#e8ecef', 900, 'rgba(0,0,0,0.5)');
      txt(c, '台北101', x + 5, y - 3.6, 4.2, '#e8c860', 900);
      // Mini tower emblem.
      for (let i = 0; i < 4; i++) poly(c, '#6fb4ac', [x - 20, y - 4 - i * 2.4, x - 15, y - 4 - i * 2.4, x - 15.8, y - 2 - i * 2.4, x - 19.2, y - 2 - i * 2.4]);
      rect(c, '#6fb4ac', x - 17.8, y - 15, 0.6, 3);
      return true;
    }
    case 'planter': {
      shadow(c, x, y, 16, 3.2);
      block(c, x - 14, y - 9, 28, 9, '#a8a49c', '#c8c4ba', '#76726c');
      rect(c, '#6a4a2a', x - 12.5, y - 9.5, 25, 1.4);
      // Clipped shrubs with little flowers.
      for (let i = 0; i < 4; i++) {
        const bx = x - 9 + i * 6;
        ellipse(c, '#2f7a3a', bx, y - 11, 4, 3.4);
        ellipse(c, '#4f9a4a', bx - 0.8, y - 12, 2.6, 2);
      }
      for (let i = 0; i < 6; i++) ellipse(c, i % 2 ? '#ff7aa0' : '#ffe08a', x - 11 + i * 4.4, y - 12.5 + (i % 3) * 1.2, 0.8, 0.8);
      return true;
    }
    case 'trafficLight': {
      const { green, left } = trafficPhase(t, x);
      shadow(c, x, y, 4, 1.4, 0.3, 40);
      rect(c, '#3a3e44', x - 1, y - 44, 2, 44);
      ellipse(c, '#2a2e33', x, y - 0.5, 3, 1);
      // Vehicle signal head on an arm.
      rect(c, '#3a3e44', x, y - 44, 9, 1.4);
      block(c, x + 5, y - 47, 16, 6, '#2a2e33');
      const red = !green;
      ellipse(c, red ? '#ff4a3a' : '#5a2020', x + 8, y - 44, 1.6, 1.6);
      ellipse(c, '#5a4a10', x + 13, y - 44, 1.6, 1.6);
      ellipse(c, green ? '#4aff8a' : '#1a4a2a', x + 18, y - 44, 1.6, 1.6);
      ellipse(c, red ? 'rgba(255,74,58,0.25)' : 'rgba(74,255,138,0.25)', red ? x + 8 : x + 18, y - 44, 4, 4);
      // Pedestrian signal: Taiwan's little walking man and a countdown.
      block(c, x - 8, y - 30, 7, 14, '#2a2e33');
      rect(c, '#0a0c0e', x - 7.2, y - 29.2, 5.4, 6);
      if (green) {
        const step = Math.floor(t * (left <= 4 ? 8 : 4)) % 2;
        ellipse(c, '#4aff8a', x - 4.5, y - 28.2, 0.7, 0.7);
        rect(c, '#4aff8a', x - 5, y - 27.4, 1, 2.2);
        line(c, '#4aff8a', 0.6, step ? [x - 4.5, y - 25.2, x - 6, y - 23.6] : [x - 4.5, y - 25.2, x - 3, y - 23.6]);
        line(c, '#4aff8a', 0.6, step ? [x - 4.5, y - 25.2, x - 3.4, y - 23.6] : [x - 4.5, y - 25.2, x - 5.6, y - 23.6]);
      } else {
        ellipse(c, '#ff4a3a', x - 4.5, y - 28.2, 0.7, 0.7);
        rect(c, '#ff4a3a', x - 5, y - 27.4, 1, 2.4);
        rect(c, '#ff4a3a', x - 5, y - 25, 0.4, 1.6);
        rect(c, '#ff4a3a', x - 4.4, y - 25, 0.4, 1.6);
      }
      rect(c, '#0a0c0e', x - 7.2, y - 22.6, 5.4, 5.6);
      c.font = `700 4px ui-monospace, monospace`;
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      c.fillStyle = green ? '#4aff8a' : '#ff4a3a';
      c.fillText(String(left).padStart(2, '0'), x - 4.5, y - 19.6);
      return true;
    }
    case 'mrtSign': {
      // Header sign mounted on the station pavilion, above its doors.
      const by = y - 40;
      rect(c, '#55606a', x - 14, by - 2, 1, 3);
      rect(c, '#55606a', x + 13, by - 2, 1, 3);
      block(c, x - 22, by - 13, 44, 12, '#1f6fc0', '#3a8fe0', '#14508e');
      metroLogo(c, x - 15, by - 7, 4);
      txt(c, '捷運', x + 3, by - 8.6, 6.4, '#ffffff', 900);
      txt(c, 'METRO', x + 3, by - 3.6, 2.6, '#cfe6f2', 700);
      if (isNight()) ellipse(c, 'rgba(120,190,255,0.22)', x, by - 7, 26, 9);
      return true;
    }
    case 'stationSign': {
      // The big railway-station name board: high on the building, or (variant 1) on its own two posts.
      const by = d.variant ? y - 14 : y - 52;
      if (d.variant) {
        shadow(c, x, y, 26, 2.4);
        for (const px of [x - 20, x + 18]) block(c, px, by - 2, 2.4, y - by + 2, '#55606a', '#7a8088');
      }
      block(c, x - 28, by - 12, 56, 12, '#f4f5f2', '#ffffff', '#b8bcc0');
      rect(c, '#f08a24', x - 27, by - 3, 54, 1);
      rect(c, '#2f5fa8', x - 27, by - 2, 54, 0.6);
      // Original rail emblem: a blue disc with two rails converging.
      ellipse(c, '#2f5fa8', x - 20, by - 6.6, 4.2, 4.2);
      line(c, '#ffffff', 0.7, [x - 22.5, by - 3.6, x - 20.6, by - 9.6]);
      line(c, '#ffffff', 0.7, [x - 17.5, by - 3.6, x - 19.4, by - 9.6]);
      rect(c, '#ffffff', x - 22, by - 6.2, 4, 0.5);
      txt(c, '火車站', x + 4, by - 8, 6.4, '#1f3a5e', 900);
      txt(c, 'RAILWAY STATION', x + 4, by - 3.8, 2.4, '#55606a', 700);
      if (isNight()) ellipse(c, 'rgba(255,240,200,0.2)', x, by - 6, 32, 9);
      return true;
    }
    case 'shopSign': {
      // A tall vertical Taiwanese street sign jutting from the shop front.
      const top = y - 88, by = y - 48;
      rect(c, '#3a3e44', x - 5, top + 4, 10, 1);
      rect(c, '#3a3e44', x - 5, by - 6, 10, 1);
      block(c, x - 4.5, top, 9, by - top, '#c0392b', '#e05a48', '#8a2a1e');
      rect(c, '#ffd34e', x - 3.5, top + 1.5, 7, 0.5);
      rect(c, '#ffd34e', x - 3.5, by - 2, 7, 0.5);
      const chars = (d.variant ?? 0) ? ['伴', '手', '禮'] : ['老', '店', '小', '吃'];
      chars.forEach((ch, i) => txt(c, ch, x, top + 6 + i * ((by - top - 8) / chars.length), 6, '#fff3b0', 900, 'rgba(0,0,0,0.4)'));
      if (isNight()) ellipse(c, 'rgba(255,120,80,0.22)', x, (top + by) / 2, 9, 22);
      return true;
    }
    case 'escalator': {
      const s = d.solid ?? { x: x - 24, y: y - 80, w: 48, h: 80 };
      const L = s.x, R = s.x + s.w, top = s.y, bot = s.y + s.h;
      shadow(c, (L + R) / 2, bot, s.w / 2, 3, 0.2);
      // Steps (moving up), darker as they climb into the ceiling opening.
      const sl = L + 6, sr = R - 6;
      vgrad(c, sl, top, sr - sl, bot - top, '#2a2e33', '#8a9096');
      const off = (t * 10) % 5;
      for (let yy = bot - 6 - off; yy > top + 2; yy -= 5) {
        rect(c, 'rgba(220,225,230,0.55)', sl, yy, sr - sl, 0.7);
        rect(c, 'rgba(255,211,78,0.7)', sl, yy, 1.4, 0.7);
        rect(c, 'rgba(255,211,78,0.7)', sr - 1.4, yy, 1.4, 0.7);
      }
      for (let i = sl + 2; i < sr - 1; i += 2) rect(c, 'rgba(0,0,0,0.12)', i, top, 0.4, bot - top);
      // Comb plate and landing.
      vgrad(c, sl - 1, bot - 6, sr - sl + 2, 6, '#c8cdd2', '#8a9096');
      rect(c, '#ffd34e', sl, bot - 6, sr - sl, 0.8);
      // Glass balustrades with black handrails.
      for (const bx of [L, R - 6]) {
        rect(c, 'rgba(190,225,240,0.35)', bx + 1, top, 4, bot - top - 2);
        block(c, bx, top, 1.6, bot - top, '#1b1d24');
        block(c, bx + 4.4, top, 1.6, bot - top, '#1b1d24');
        rect(c, '#c8cdd2', bx + 1.6, bot - 4, 2.8, 4);
      }
      // Ceiling opening shadow and an exit sign.
      vgrad(c, L, top, s.w, 10, 'rgba(0,0,0,0.55)', 'rgba(0,0,0,0)');
      block(c, (L + R) / 2 - 13, top + 2, 26, 7, '#ffd34e', '#fff0a0');
      txt(c, '出口 ↑', (L + R) / 2, top + 5.7, 4, '#1b1d24', 900);
      return true;
    }
    case 'screenDoors': {
      const w = d.w ?? 384;
      const x0 = x, x1 = x + w, by = y;
      // Train timetable: arrive, stop with the doors open, leave, then an empty track.
      const p = t % 20;
      let off: number | null = null, open = 0;
      if (p < 3.5) off = (1 - (1 - Math.pow(1 - p / 3.5, 3))) * 520;
      else if (p < 10) {
        off = 0;
        open = Math.max(0, Math.min(1, (p - 4.3) / 0.8, (9.3 - p) / 0.8));
      } else if (p < 13.5) off = -Math.pow((p - 10) / 3.5, 3) * 560;
      // Dark track trench behind the glass.
      vgrad(c, x0, by - 22, w, 22, '#1a2026', '#2a3038');
      if (off !== null) {
        const tx0 = x0 - 16 + off, tx1 = x1 + 16 + off;
        c.save();
        c.beginPath();
        c.rect(x0 - 16, by - 40, w + 32, 72);
        c.clip();
        train(c, METRO, Math.max(tx0, x0 - 60), Math.min(tx1, x1 + 60), by + 28, t, SD_FIRST + off, open, tx0 > x0 - 60, tx1 < x1 + 60);
        c.restore();
      }
      // Glass wall: header, fixed panels and sliding door leaves at each door.
      for (let dx = SD_FIRST; dx < x1; dx += SD_STEP) {
        const leaf = 8 * (1 - open);
        // Fixed panels to either side of this door.
        const pl = dx - SD_STEP / 2 + 1, pr = dx - 8;
        rect(c, 'rgba(170,215,235,0.28)', pl, by - 22, pr - pl, 21);
        rect(c, 'rgba(170,215,235,0.28)', dx + 8, by - 22, SD_STEP / 2 - 9, 21);
        // Door leaves.
        for (const s of [-1, 1]) {
          const lx = s < 0 ? dx - 8 - (8 - leaf) : dx + (8 - leaf);
          if (leaf < 0.3) continue;
          rect(c, 'rgba(190,230,250,0.4)', lx, by - 22, leaf, 21);
          rect(c, '#c8cdd2', s < 0 ? lx : lx + leaf - 0.8, by - 22, 0.8, 21);
          rect(c, '#e2493b', lx + leaf / 2 - 1, by - 11, 2, 0.6);
        }
        // Frame posts.
        rect(c, '#9aa0a6', dx - 8.8, by - 23, 1.2, 23);
        rect(c, '#9aa0a6', dx + 7.6, by - 23, 1.2, 23);
        // Door header with an LED strip.
        rect(c, '#2a2e33', dx - 9, by - 27, 18, 4);
        rect(c, open > 0 && Math.sin(t * 8) > 0 ? '#ff8a3a' : '#7a3a1a', dx - 2, by - 26.2, 4, 0.8);
        rect(c, '#55ffa0', dx - 8, by - 24.6, 2.2, 0.6);
      }
      // Continuous top beam with route displays, and the yellow platform edge.
      rect(c, '#3a3e44', x0, by - 30, w, 3.4);
      rect(c, '#8a9096', x0, by - 30, w, 0.6);
      for (let dx = SD_FIRST + SD_STEP / 2; dx < x1; dx += SD_STEP * 2) {
        rect(c, '#0a0c0e', dx - 8, by - 29.6, 16, 2.6);
        txt(c, '往 淡水', dx, by - 28.2, 2.2, '#ffb43a', 700);
      }
      rect(c, 'rgba(255,255,255,0.18)', x0, by - 22, w, 0.6);
      rect(c, '#ffd34e', x0, by - 1.4, w, 1.4);
      return true;
    }
    case 'stanchion': {
      const w = d.w ?? 48;
      const posts = [x - w / 2, x, x + w / 2];
      for (const px of posts) shadow(c, px, y, 2.4, 0.9);
      // Red velvet ropes sagging between brass posts.
      for (let i = 0; i < posts.length - 1; i++) {
        const a = posts[i], b = posts[i + 1];
        c.strokeStyle = '#8a1a20';
        c.lineWidth = 1.4;
        c.beginPath();
        c.moveTo(a, y - 10);
        c.quadraticCurveTo((a + b) / 2, y - 3, b, y - 10);
        c.stroke();
        c.strokeStyle = 'rgba(255,120,120,0.45)';
        c.lineWidth = 0.4;
        c.beginPath();
        c.moveTo(a, y - 10.4);
        c.quadraticCurveTo((a + b) / 2, y - 3.6, b, y - 10.4);
        c.stroke();
      }
      for (const px of posts) {
        ellipse(c, '#b8902a', px, y - 0.6, 2.4, 0.9);
        rect(c, '#d9b450', px - 0.6, y - 11, 1.2, 10.5);
        rect(c, '#fff0b0', px - 0.6, y - 11, 0.4, 10);
        ellipse(c, '#e8c860', px, y - 11.5, 1.2, 1.2);
      }
      return true;
    }
    case 'pillar': {
      shadow(c, x, y, 8, 2.4);
      block(c, x - 8, y - 6, 16, 6, '#d8d2c4', '#ece6d8', '#a8a294');
      block(c, x - 6, y - 58, 12, 52, '#a8261c', '#c84030', '#7a1a12');
      rect(c, 'rgba(255,200,170,0.25)', x - 4, y - 56, 1.4, 48);
      rect(c, 'rgba(0,0,0,0.18)', x + 2.5, y - 56, 2, 48);
      // Gilded capital with a little cloud pattern.
      block(c, x - 8, y - 64, 16, 6, '#c9a24a', '#ecd28a', '#8a6a20');
      for (let i = -5; i <= 5; i += 5) ellipse(c, '#8a6a20', x + i, y - 61, 1.4, 0.9);
      rect(c, '#d9b450', x - 6, y - 8, 12, 0.8);
      return true;
    }
    case 'museumBench': {
      const w = d.w ?? 64;
      shadow(c, x, y, w / 2, 2.6);
      for (const lx of [x - w / 2 + 3, x + w / 2 - 6]) block(c, lx, y - 6, 3, 6, '#2a1810');
      block(c, x - w / 2, y - 10, w, 5, '#4a2a1a', '#6a3e26', '#2a1810');
      block(c, x - w / 2 + 2, y - 12.5, w - 4, 3, '#7a1e22', '#a03a3a');
      for (let i = x - w / 2 + 8; i < x + w / 2 - 4; i += 8) ellipse(c, 'rgba(0,0,0,0.25)', i, y - 11.2, 0.5, 0.4);
      return true;
    }
    case 'waterfall': {
      const h = d.h ?? 100;
      fall(c, x, y - h, y, 7, t);
      return true;
    }
    case 'shrine': {
      const cx = d.solid ? d.solid.x + d.solid.w / 2 : x;
      // A waterfall spilling from the cliff beside the shrine.
      fall(c, cx - 23, y - 50, y + 2, 6, t);
      // Rocky ledge and stone base with steps.
      shadow(c, cx, y, 20, 3);
      block(c, cx - 19, y - 8, 38, 8, '#b8b4aa', '#d8d4ca', '#8a867c');
      for (let i = 0; i < 3; i++) rect(c, '#e8e4da', cx - 6 + i, y - 2 - i * 2, 12 - i * 2, 0.8);
      // Red columns and white walls.
      block(c, cx - 15, y - 26, 30, 18, '#f2ede2', '#ffffff', '#c8c0b0');
      for (const px of [cx - 15, cx - 6, cx + 4, cx + 13]) block(c, px, y - 27, 2.4, 19, '#b0302a', '#d04a3a');
      rect(c, '#3a2418', cx - 3.5, y - 22, 7, 14);
      rect(c, '#5a3a24', cx - 3, y - 21.5, 6, 0.6);
      // Lattice windows.
      for (const wx2 of [cx - 12, cx + 7]) {
        rect(c, '#5a3a24', wx2, y - 22, 4.6, 6);
        for (let k = 0; k < 3; k++) rect(c, '#c8a070', wx2 + 0.6 + k * 1.6, y - 21.4, 0.4, 4.8);
      }
      // Bracket band and the dark tiled hip roof with upturned eaves.
      rect(c, '#2f6a5a', cx - 16, y - 29, 32, 2.4);
      for (let i = cx - 15; i < cx + 15; i += 3) rect(c, '#d9b450', i, y - 28.4, 1, 1);
      poly(c, '#2a3a36', [cx - 24, y - 30, cx + 24, y - 30, cx + 14, y - 40, cx - 14, y - 40]);
      vgrad(c, cx - 14, y - 40, 28, 2, '#5a6a64', '#3a4a46');
      for (let i = cx - 20; i < cx + 20; i += 2.6) rect(c, 'rgba(0,0,0,0.25)', i, y - 37 + Math.abs(i - cx) * 0.1, 0.5, 6 - Math.abs(i - cx) * 0.1);
      poly(c, '#2a3a36', [cx - 24, y - 30, cx - 27, y - 34, cx - 21, y - 32]);
      poly(c, '#2a3a36', [cx + 24, y - 30, cx + 27, y - 34, cx + 21, y - 32]);
      rect(c, '#1e2a26', cx - 15, y - 41.5, 30, 1.8);
      for (const s of [-1, 1]) poly(c, '#1e2a26', [cx + s * 15, y - 41.5, cx + s * 18, y - 44, cx + s * 15, y - 39.8]);
      rect(c, '#d9b450', cx - 0.6, y - 45, 1.2, 3.5);
      return true;
    }
    case 'tunnel': {
      // A marble rock arch over the trail (drawn above walkers). Spans the trail tiles to the west of the anchor tile.
      const w = d.w ?? 48;
      const L = x - T, R = L + w, cx = (L + R) / 2, top = y - 44, archTop = y - 30;
      const jag = (k: number) => Math.sin(k * 0.9 + w) * 1.6 + Math.sin(k * 2.3) * 1;
      const shape = () => {
        c.beginPath();
        c.moveTo(L - 7, y);
        c.lineTo(L - 9 + jag(1), y - 14);
        c.lineTo(L - 8 + jag(2), y - 30);
        c.lineTo(L - 12, top + 6);
        for (let px = L - 10, k = 0; px <= R + 10; px += 6, k++) c.lineTo(px, top + jag(k) - (k % 2) * 1.5);
        c.lineTo(R + 12, top + 7);
        c.lineTo(R + 8 + jag(5), y - 28);
        c.lineTo(R + 9 + jag(6), y - 13);
        c.lineTo(R + 7, y);
        c.lineTo(R - 1, y);
        c.lineTo(R - 1, archTop + 6);
        c.quadraticCurveTo(R - 2, archTop - 2, cx, archTop - 3);
        c.quadraticCurveTo(L + 2, archTop - 2, L + 1, archTop + 6);
        c.lineTo(L + 1, y);
        c.closePath();
      };
      c.save();
      c.translate(0, 0.8);
      shape();
      c.fillStyle = 'rgba(30,26,22,0.35)';
      c.fill();
      c.restore();
      shape();
      c.fillStyle = '#7a766c';
      c.fill();
      c.save();
      shape();
      c.clip();
      vgrad(c, L - 14, top - 4, w + 28, y - top + 4, '#ece8de', '#9a968a');
      // Marble strata and veins, like the cliff faces.
      for (let i = 0; i < 14; i++) {
        const sx = L - 12 + ((i * 23) % (w + 24));
        rect(c, i % 2 ? 'rgba(255,255,255,0.4)' : 'rgba(70,64,56,0.22)', sx, top + (i % 3) * 2, 0.8, 14 + (i % 4) * 7);
      }
      c.strokeStyle = 'rgba(255,255,255,0.55)';
      c.lineWidth = 0.4;
      c.beginPath();
      c.moveTo(L - 10, top + 9);
      c.quadraticCurveTo(cx - 6, top + 4, cx + 4, top + 10);
      c.quadraticCurveTo(R, top + 14, R + 10, top + 8);
      c.stroke();
      // Shade on the inner faces of the pillars and under the lintel.
      rect(c, 'rgba(40,36,30,0.3)', L - 2, archTop - 4, 3, y - archTop + 4);
      rect(c, 'rgba(40,36,30,0.22)', R - 3, archTop - 4, 3, y - archTop + 4);
      c.restore();
      c.strokeStyle = 'rgba(40,34,28,0.55)';
      c.lineWidth = 1.6;
      c.beginPath();
      c.moveTo(L + 1.5, archTop + 6);
      c.quadraticCurveTo(L + 2, archTop - 1.5, cx, archTop - 2.2);
      c.quadraticCurveTo(R - 2, archTop - 1.5, R - 1.5, archTop + 6);
      c.stroke();
      rect(c, 'rgba(255,255,255,0.6)', L - 11, top + 6, w + 22, 0.6);
      // Moss and ferns on top, a few hanging down.
      for (const [fx, fy] of [[L - 4, top + 1], [L + w * 0.4, top - 1], [R + 4, top + 2], [R + 7, y - 26]]) {
        ellipse(c, '#3f7a35', fx, fy, 4, 1.8);
        ellipse(c, '#5f9a45', fx - 1, fy - 0.6, 2.4, 1);
      }
      line(c, '#4f8a3f', 0.6, [L + w * 0.6, archTop - 2, L + w * 0.6 + 0.6, archTop + 4]);
      line(c, '#4f8a3f', 0.6, [L + w * 0.63, archTop - 2, L + w * 0.67, archTop + 2.5]);
      return true;
    }
    case 'ropeBridgeRails': {
      const w = d.w ?? 80;
      const L = x, R = x + w;
      const rail = (yy: number, sag: number, col: string) => {
        c.strokeStyle = col;
        c.lineWidth = 0.8;
        c.beginPath();
        c.moveTo(L, yy);
        c.quadraticCurveTo((L + R) / 2, yy + sag, R, yy);
        c.stroke();
      };
      // Hangers from the handrails down to the deck edges.
      for (let i = 1; i < 10; i++) {
        const k = i / 10, hx = L + k * w, s = 4 * k * (1 - k) * 3;
        rect(c, 'rgba(232,216,176,0.8)', hx - 0.2, y - 6 + s, 0.4, 7 - s);
        rect(c, 'rgba(232,216,176,0.8)', hx - 0.2, y + 8 + s, 0.4, 6 - s);
      }
      rail(y - 6, 6, '#c8a878');
      rail(y + 8, 6, '#e8d8b0');
      rail(y + 8.6, 6, 'rgba(0,0,0,0.25)');
      // Posts at both ends with their anchor cables.
      for (const px of [L - 1, R - 1]) {
        for (const py of [y - 1, y + 13]) {
          block(c, px - 1.2, py - 7, 4.4, 9, '#6a4a2a', '#8a6440');
          ellipse(c, '#e8d8b0', px + 1, py - 7, 1.6, 0.8);
        }
      }
      return true;
    }
    case 'trailSign': {
      shadow(c, x, y, 6, 1.6);
      for (const px of [x - 9, x + 7]) block(c, px, y - 26, 2.4, 26, '#6a4a2a', '#8a6440');
      block(c, x - 14, y - 34, 28, 9, '#7a5034', '#9a6a44', '#55361f');
      txt(c, '太魯閣', x, y - 29.4, 5.6, '#fff3d0', 900);
      // Arrow planks.
      poly(c, '#8a5a34', [x - 14, y - 23, x + 10, y - 23, x + 14, y - 20, x + 10, y - 17, x - 14, y - 17]);
      txt(c, '燕子口 →', x - 1, y - 19.9, 3.8, '#fff3d0', 900);
      poly(c, '#8a5a34', [x + 14, y - 15, x - 10, y - 15, x - 14, y - 12, x - 10, y - 9, x + 14, y - 9]);
      txt(c, '← 長春祠', x + 1, y - 11.9, 3.8, '#fff3d0', 900);
      return true;
    }
    case 'lookout': {
      const s = d.solid;
      const cx = s ? s.x + s.w / 2 : x;
      const w = s ? s.w + 8 : 56;
      shadow(c, cx, y, w / 2 + 2, 3);
      // Raised wooden deck on stilts with a railing and a little roof.
      for (const px of [cx - w / 2 + 2, cx - 2, cx + w / 2 - 5]) block(c, px, y - 10, 3, 10, '#5a3a20', '#7a5030');
      block(c, cx - w / 2, y - 14, w, 5, '#a8784a', '#c8986a', '#7a5030');
      for (let i = cx - w / 2 + 3; i < cx + w / 2 - 1; i += 3) rect(c, 'rgba(0,0,0,0.18)', i, y - 13.5, 0.5, 4);
      rect(c, '#6a4a2a', cx - w / 2, y - 24, w, 1.4);
      for (let i = cx - w / 2; i <= cx + w / 2 - 1; i += 6) rect(c, '#6a4a2a', i, y - 24, 1.2, 10);
      // Posts and a pavilion roof.
      for (const px of [cx - w / 2 + 1, cx + w / 2 - 3]) block(c, px, y - 40, 2.4, 26, '#5a3a20', '#7a5030');
      poly(c, '#3a6a5a', [cx - w / 2 - 6, y - 38, cx + w / 2 + 6, y - 38, cx + w / 2 - 4, y - 47, cx - w / 2 + 4, y - 47]);
      rect(c, '#2a4a40', cx - w / 2 - 6, y - 38.5, w + 12, 1.4);
      for (let i = cx - w / 2 - 2; i < cx + w / 2 + 2; i += 3) rect(c, 'rgba(0,0,0,0.18)', i, y - 46, 0.5, 7);
      // Steps down to the beach.
      for (let i = 0; i < 3; i++) block(c, cx + w / 2 - 2 + i * 2, y - 9 + i * 3, 6, 2.4, '#a8784a');
      return true;
    }
    case 'stoneCairn':
      cairn(c, x, y, d.variant ?? 0);
      if (d.variant) cairn(c, x + 10, y + 1, 0);
      return true;
    default:
      return false;
  }
}

/** Things that change every frame (never cached). */
export const ANIMATED4_OBJ = ['stTrain', 'twElevator', 'tpNoodles', 'tpCakes', 'hlMochi'];
export const ANIMATED4_DECOR = ['trafficLight', 'escalator', 'screenDoors', 'waterfall', 'shrine'];
/** Decor drawn above characters. */
export const OVERHEAD4_DECOR = new Set<string>(['tunnel']);

// ---------------------------------------------------------------------------
// Wall-mounted decor (painted with the structures, so static)
// ---------------------------------------------------------------------------

export function wallDecor4(c: Ctx, d: Decor): boolean {
  const { x, y } = d;
  switch (d.kind) {
    case 'adPanel': {
      const w = d.w ?? 48;
      block(c, x + 1, y, w - 2, 12, '#c8cdd2', '#e8ecef', '#8a9096');
      const ix = x + 2.5, iy = y + 1.5, iw = w - 5, ih = 9;
      if (d.variant) {
        // Travel poster: Hualien's mountains meeting the sea.
        vgrad(c, ix, iy, iw, ih, '#8fd0f0', '#e8f6ff');
        poly(c, '#7a8a80', [ix, iy + ih - 2, ix + iw * 0.2, iy + 2, ix + iw * 0.38, iy + ih - 3, ix + iw * 0.5, iy + 3.5, ix + iw * 0.65, iy + ih - 2]);
        rect(c, '#2a9ac0', ix, iy + ih - 2.5, iw, 2.5);
        rect(c, 'rgba(255,255,255,0.7)', ix + iw * 0.6, iy + ih - 2.6, iw * 0.4, 0.5);
        txt(c, '花蓮', ix + iw * 0.82, iy + 3.2, 4, '#c0392b', 900);
      } else {
        // Bubble-tea ad.
        vgrad(c, ix, iy, iw, ih, '#ffe0a8', '#ffb07a');
        block(c, ix + 4, iy + 1, 6, 8, 'rgba(255,250,240,0.95)');
        rect(c, '#c49a6c', ix + 4.5, iy + 4, 5, 4.5);
        for (let k = 0; k < 3; k++) ellipse(c, '#2a1810', ix + 5.5 + k * 1.5, iy + 7.6, 0.5, 0.5);
        rect(c, '#e2493b', ix + 7, iy - 0.5, 0.8, 4);
        txt(c, '珍珠奶茶', ix + iw * 0.62, iy + 3.4, 4.2, '#7a2a10', 900);
        txt(c, 'NT$ 50', ix + iw * 0.62, iy + 7, 2.6, '#c0392b', 900);
      }
      rect(c, 'rgba(255,255,255,0.3)', ix, iy, iw, 0.6);
      return true;
    }
    case 'hangingScroll': {
      const v = d.variant ?? 0;
      const w = 10, top = y, bot = y + 14;
      rect(c, 'rgba(0,0,0,0.3)', x + 1, top + 1, w, bot - top);
      rect(c, '#6a3a20', x - 1, top, w + 2, 1.4);
      line(c, '#2a1810', 0.3, [x + w / 2, top - 1.5, x + 1, top, x + w / 2, top - 1.5, x + w - 1, top]);
      rect(c, v === 1 ? '#c8b080' : '#7a8aa0', x, top + 1.4, w, bot - top - 2.4);
      const px = x + 1.2, py = top + 2.8, pw = w - 2.4, ph = bot - top - 5.2;
      if (v === 1) {
        // Calligraphy: a column of bold brush characters and a red seal.
        rect(c, '#f4ead0', px, py, pw, ph);
        txt(c, '山', x + w / 2, py + 2.3, 3.6, '#1b1410', 900);
        txt(c, '水', x + w / 2, py + 5.6, 3.6, '#1b1410', 900);
        rect(c, '#c0392b', px + pw - 2, py + ph - 2, 1.4, 1.4);
      } else if (v === 2) {
        // Bamboo in ink.
        rect(c, '#f0e6cc', px, py, pw, ph);
        for (const bx of [px + 2, px + 5]) {
          rect(c, '#3a4a3a', bx, py, 0.7, ph);
          for (let k = 1.5; k < ph; k += 2.6) rect(c, '#1e2a1e', bx - 0.3, py + k, 1.3, 0.4);
        }
        for (const [lx, ly, s] of [[2.6, 1.5, 1], [5.6, 4, -1], [2.6, 5.5, -1]]) line(c, '#2a3a2a', 0.5, [px + lx, py + ly, px + lx + s * 2.2, py + ly + 1]);
      } else inkLandscape(c, px, py, pw, ph, x * 0.07);
      rect(c, '#6a3a20', x - 0.6, bot - 1, w + 1.2, 1.4);
      ellipse(c, '#e8d8b0', x - 0.6, bot - 0.3, 0.7, 0.7);
      ellipse(c, '#e8d8b0', x + w + 0.6, bot - 0.3, 0.7, 0.7);
      return true;
    }
    case 'museumBanner': {
      const w = d.w ?? 64;
      rect(c, 'rgba(0,0,0,0.3)', x + 3, y + 1, w - 4, 13);
      rect(c, '#2a1810', x, y, w, 1.2);
      vgrad(c, x + 2, y + 1, w - 4, 12, '#f2e2b8', '#d8c090');
      rect(c, '#a8261c', x + 2, y + 1, w - 4, 1);
      rect(c, '#a8261c', x + 2, y + 12, w - 4, 1);
      txt(c, '國立故宮博物院', x + w / 2, y + 5.6, 6.4, '#5a1a12', 900);
      txt(c, 'NATIONAL PALACE MUSEUM', x + w / 2, y + 10.3, 2.6, '#7a4a2a', 700);
      for (const tx of [x + 2, x + w - 3]) {
        rect(c, '#d9b450', tx, y + 13, 1, 2);
        ellipse(c, '#c0392b', tx + 0.5, y + 15.2, 0.8, 0.8);
      }
      return true;
    }
    case 'departures': {
      // Split-flap departures board.
      const w = d.w ?? 80;
      block(c, x, y, w, 14, '#1b1d24', '#2a2e36');
      rect(c, '#2f5fa8', x + 1, y + 1, w - 2, 2.6);
      txt(c, '列車時刻 DEPARTURES', x + w / 2, y + 2.4, 2.2, '#ffffff', 700);
      const rows = [['自強', '臺北', '10:12', '#e2493b'], ['區間', '花蓮', '10:30', '#3a8fd9'], ['莒光', '高雄', '11:05', '#f08a24']];
      rows.forEach(([kind, to, time, col], i) => {
        const ry = y + 5.4 + i * 2.9;
        rect(c, '#0a0c0e', x + 2, ry - 1.2, w - 4, 2.5);
        rect(c, col, x + 3, ry - 0.9, 7, 1.9);
        c.font = `700 2.2px ${CJK}`;
        c.textBaseline = 'middle';
        c.textAlign = 'center';
        c.fillStyle = '#ffffff';
        c.fillText(kind, x + 6.5, ry);
        c.fillStyle = '#ffb43a';
        c.textAlign = 'left';
        c.fillText(to, x + 13, ry);
        c.textAlign = 'right';
        c.fillText(time, x + w - 4, ry);
        for (let k = 0; k < 6; k++) rect(c, 'rgba(255,255,255,0.06)', x + 13 + k * ((w - 18) / 6), ry - 1.2, 0.3, 2.5);
      });
      return true;
    }
    default:
      return wallDecorTown(c, d) || drawExtraDecor(c, d, 0);
  }
}
